// #1107 follow-on — i18n strings for the /intake/review form (the
// family-facing structured intake) and its shared sub-editors
// (Family / Friends / Pets / Faith / Childhood / Working life / Health),
// the follow-up-questions screen, and the "all set" screen.
//
// Before this, the whole page was hardcoded English, so a family that
// picked Dutch on the website still saw an English intake. These strings
// are keyed by the EXACT same structure in every language; missing keys
// fall back to English via the deep-merge in translations.js
// (getTranslations), and a parity test pins that every leaf exists in
// every supported language.
//
// Lives in its own module (not inline in translations.js) because the
// surface is large; translations.js imports this and injects it as the
// `intakeReview` namespace per language.
//
// Placeholder conventions: {count}, {n}, {companion}, {name}, {relation},
// {relTo} are substituted at render with formatString() — keep them
// verbatim in every translation.

const en = {
  loading: 'Loading your answers…',

  common: {
    optional: '(optional)',
    add: 'Add',
    addItem: '+ Add',
    remove: 'Remove',
    saving: 'Saving…',
  },

  header: {
    newTitleFamily: 'Tell us about your loved one',
    newTitleSelf: 'Tell us about yourself',
    newSubFamily: 'Fill in the basics so the companion knows how to talk with them. Only a few fields are required (marked with *) — the rest help the companion be more interesting, but you can always come back later.',
    newSubSelf: 'Fill in the basics so Lena knows how to talk with you. Only a few fields are required (marked with *) — the rest help her be more interesting, but you can always come back later.',
    reviewTitle: 'Review your answers',
    reviewSub: "Check everything below and correct anything that's wrong. You can always come back and edit later from the portal.",
  },

  validationSummaryTitle: 'A few required fields are missing',

  // Consumed by intakeValidation.js via getIntakeValidationMessages(t).
  validation: {
    firstNameFamily: "What's their first name?",
    firstNameSelf: "What's your first name?",
    birthdayMissingFamily: 'Birthday is needed so Lena can wish them on the right day.',
    birthdayMissingSelf: 'Your birthday is needed so Lena can wish you on the right day.',
    birthdayAdultFamily: 'Amigo Mio is built for adults — please check the birthday.',
    birthdayAdultSelf: 'You need to be at least 18 to create an account.',
    languageFamily: 'Pick a language so the companion can speak with them.',
    languageSelf: 'Pick a language so Lena can speak with you.',
    interestsFamily: 'Please add at least 3 interests (you have {count}). Spread them across the categories below — these are what the companion will talk about.',
    interestsSelf: 'Please add at least 3 interests (you have {count}). Spread them across the categories below — these are what Lena will chat with you about.',
    familyFamily: 'Add at least one family member — names and relationships ground the conversation.',
    familySelf: 'Add at least one family member or friend — names and relationships ground the conversation.',
  },

  personal: {
    section: 'Personal',
    firstName: 'First name',
    lastName: 'Last name',
    preferredNameFamily: 'Preferred name (what they like to be called)',
    preferredNameSelf: 'Preferred name (what would you like to be called)',
    birthday: 'Birthday',
    birthdayHelp: 'Helps the companion pick era-appropriate music, films, and topics — and lets Lena wish them happy birthday on the right day.',
    under18Family: "Amigo Mio is built for adult users — please check the date of birth. The account won't be set up until this shows an adult.",
    under18Self: "You need to be at least 18 to create an account. The account won't be set up until the date of birth shows an adult.",
    language: 'Language',
    companionName: 'Companion name',
    companionNamePlaceholder: 'Lena',
  },

  location: {
    section: 'Location',
    cityCountry: 'In which city and country do they live now?',
    cityCountryPlaceholder: 'e.g. Barcelona, Spain',
    timezone: 'Timezone',
    timezoneHelp: 'We use this to greet them at the right time of day and to schedule reminder calls within their day. Detected from your browser — change it if they live elsewhere.',
    otherPlaces: "Other places they've lived",
  },

  interests: {
    section: 'Interests',
    sectionHint: '(at least 3 across the categories below — what the companion will talk about)',
    help: "Categories help the companion know what kind of interest each one is — when to play music vs. show a film vs. talk sport. You don't need to fill every category — just the ones that fit.",
    minCount: '{count} / 3 minimum',
    topicsToAvoid: 'Topics to avoid',
    buckets: {
      music_artists: { label: 'Favourite singers / bands', placeholder: 'e.g. Queen, Frank Sinatra' },
      music_genres: { label: 'Music styles / genres', placeholder: 'e.g. classical, 1950s jazz' },
      sports_interests: { label: 'Sports / teams', placeholder: 'e.g. Liverpool FC, Wimbledon tennis' },
      screen_interests: { label: 'Films / TV / actors', placeholder: 'e.g. Charlie Chaplin, Casablanca' },
      places_interests: { label: 'Favourite places', placeholder: 'e.g. Lake District, Barcelona' },
      hobbies: { label: 'Hobbies', placeholder: 'e.g. gardening, crosswords' },
      other_interests: { label: 'Other interests', placeholder: 'anything else worth knowing' },
    },
  },

  family: {
    section: 'Family',
    sectionHint: '(at least one — names + relationships ground every conversation)',
    empty: 'No family members added yet. Add at least one — names + relationships ground every conversation.',
    name: 'Name',
    namePlaceholder: 'e.g. Maria',
    relation: 'Relation',
    whose: 'Whose?',
    whoseHintFamily: '(defaults to your loved one)',
    whoseHintSelf: '(defaults to you)',
    whosePlaceholderFamily: 'Your loved one',
    whosePlaceholderSelf: 'You',
    stillWithUs: 'Still with us?',
    statusAlive: 'Yes',
    statusDeceased: 'Passed away',
    notes: 'Notes',
    notesHint: '(optional — anything the companion should know)',
    notesPlaceholder: 'e.g. lives in Barcelona, calls every Sunday',
    addBtn: '+ Add family member',
    readbackAlive: '{name}, who is still with us, is the {relation} of {relTo}.',
    readbackDeceased: '{name}, who has passed away, was the {relation} of {relTo}.',
  },

  friends: {
    section: 'Friends',
    sectionHint: '(optional — old friends Lena can ask after)',
    empty: 'No friends added yet. Optional, but Lena loves asking after old friends.',
    name: 'Name',
    namePlaceholder: 'e.g. Jan Boer',
    status: 'Status',
    statusInContact: 'Still in touch',
    statusLostTouch: 'Lost touch',
    statusDeceased: 'Passed away',
    location: 'Location',
    locationPlaceholder: 'Where they live now',
    howMet: 'How they met',
    howMetPlaceholder: 'e.g. school friends, neighbours, colleagues',
    sharedInterests: 'Shared interests',
    sharedInterestsPlaceholder: 'What they used to do / talk about together',
    addBtn: '+ Add friend',
  },

  pets: {
    section: 'Pets',
    sectionHint: '(optional — current and past)',
    empty: 'No pets added yet. Optional, but pets are some of the warmest things Lena can ask about.',
    name: 'Name',
    namePlaceholder: 'e.g. Buddy',
    species: 'Species',
    speciesPlaceholder: 'Dog, Cat, Budgie…',
    status: 'Status',
    statusCurrent: 'Current',
    statusPast: 'Past',
    note: 'Note',
    notePlaceholder: 'e.g. rescue from the shelter, loves walks',
    addBtn: '+ Add pet',
  },

  faith: {
    section: 'Faith / spirituality',
    tradition: 'Tradition',
    notSpecified: '(not specified)',
    otherSpecify: 'Other (specify)…',
    traditionCustomPlaceholder: 'Type the tradition',
    traditionHelp: 'Empty / "none" / "not religious" all mean Lena will make no faith references.',
    importance: 'How important is it day-to-day?',
    importanceCentral: 'Central to daily life',
    importanceCultural: 'Cultural / holidays only',
    importanceNotReally: 'Not really part of daily life',
    practicesFamily: 'Practices & traditions she keeps',
    practicesSelf: 'Practices & traditions you keep',
    practicesPlaceholderFamily: "e.g. goes to Christmas Eve Mass with her daughter; lights candles for All Saints' Day.",
    practicesPlaceholderSelf: "e.g. you go to Christmas Eve Mass with your daughter; light candles for All Saints' Day.",
  },

  childhood: {
    section: 'Childhood',
    growUpFamily: 'Where they grew up — family, home, anything that shaped them',
    growUpSelf: 'Where you grew up — family, home, anything that shaped you',
    growUpPlaceholderFamily: 'e.g. eldest of four in Halifax, Yorkshire — father a coal miner, summers in Scarborough, learned to bake with her gran. Skip if not a happy area.',
    growUpPlaceholderSelf: 'e.g. eldest of four in Halifax, Yorkshire — father a coal miner, summers in Scarborough, you learned to bake with your gran. Skip if not a happy area.',
    sensitiveNotes: 'Sensitive notes',
    sensitiveBadge: 'private',
    sensitivePlaceholder: 'Anything Lena should know but should NEVER bring up — e.g. a strict parent, a loss, a difficult time.',
    sensitiveHelp: '🔒 Encrypted, never shown back to you or read aloud. Lena receives this only as guidance on what to avoid.',
  },

  working: {
    section: 'Working life',
    profession: 'Profession or role',
    professionPlaceholder: 'e.g. primary school teacher',
    employer: 'Employer',
    employerPlaceholder: "e.g. St Mary's Primary, Halifax",
    industry: 'Industry',
    industryPlaceholder: 'e.g. Education',
    yearsActive: 'Years active',
    yearsActivePlaceholder: 'e.g. 1962 – 1998',
    proudFamily: 'What is she most proud of?',
    proudSelf: 'What are you most proud of?',
    proudPlaceholderFamily: 'e.g. brought hundreds of children to read; still gets letters from former pupils.',
    proudPlaceholderSelf: 'e.g. brought hundreds of children to read; still get letters from former pupils.',
  },

  memories: {
    section: 'Happy memories & life events',
    label: 'Special moments or memories',
    placeholder: 'Memorable events, trips, milestones…',
  },

  health: {
    section: 'Health & memory notes',
    ongoing: 'Ongoing health conditions',
    ongoingHelp: "Long-standing conditions the companion should be aware of for pacing and sensitivity (diabetes, arthritis, hearing loss…). She uses these silently — she won't bring them up as a topic.",
    chronicEmpty: "No ongoing health conditions added yet. Optional — only add things that won't change (diabetes, arthritis, hearing loss, etc.). The companion won't bring them up directly.",
    chronicPlaceholder: 'e.g. Type 2 diabetes since 2015, well-controlled with medication',
    addCondition: '+ Add condition',
    notesPrivate: 'Notes for the companion (private)',
    notesPrivateHelp: 'Sensitive guidance she should know but never raise (a difficult history, things to avoid). Stays private; never echoed back.',
    notesPrivatePlaceholder: 'Anything the companion should know to adapt…',
  },

  saveButton: 'Save & go to follow-up questions',

  followup: {
    loadingTitle: 'Writing five questions about them…',
    loadingSub: 'This usually takes a few seconds.',
    emptyTitle: 'One last step…',
    emptyBody: "We couldn't generate the personal questions just now{errorSuffix} — you can finish without them and Lena will still have everything from the form.",
    continue: 'Continue',
    headerTitle: 'A few questions to make the companion feel like family',
    headerSubFamily: 'These are personal to your loved one — answer any that come to mind, skip the rest. Even one good story helps the companion sound like she’s known them for years.',
    headerSubSelf: 'These are personal questions for you — answer any that come to mind, skip the rest. Even one good story helps the companion sound like she’s known you for years.',
    questionN: 'Question {n}',
    answerPlaceholder: 'Your answer (optional)',
    showDifferent: 'Show different questions',
    generating: 'Generating…',
    skip: "Skip — I'm done",
    saveFinish: 'Save & finish',
    errGenerate: 'Could not generate questions',
    errSave: 'Could not save answers',
  },

  allset: {
    title: 'You are all set!',
    subFamily: '{companion} is ready to meet {name}!',
    subSelf: '{companion} is ready to chat with you!',
    openTabletFamily: 'Open this on their tablet',
    openTabletSelf: 'Open this on your tablet',
    qrHelp: "Point the tablet's camera at the QR code, or open a browser and type the address above. The tablet will pair automatically.",
    reassurance: "Don't have the tablet here right now? No worries — you can always get this link again from your Portal.",
    goPortal: 'Go to your portal',
  },

  toastLoadError: 'Could not load review data',
  saveFailed: 'Save failed',
};

const nl = {
  "loading": "Uw antwoorden worden geladen…",
  "common": {
    "optional": "(optioneel)",
    "add": "Toevoegen",
    "addItem": "+ Toevoegen",
    "remove": "Verwijderen",
    "saving": "Bezig met opslaan…"
  },
  "header": {
    "newTitleFamily": "Vertel ons over uw dierbare",
    "newTitleSelf": "Vertel ons iets over uzelf",
    "newSubFamily": "Vul de basisgegevens in zodat de metgezel weet hoe hij met hem of haar moet praten. Slechts enkele velden zijn verplicht (gemarkeerd met *) — de rest helpt de metgezel om interessanter te zijn, maar u kunt altijd later terugkomen.",
    "newSubSelf": "Vul de basisgegevens in zodat Lena weet hoe ze met u moet praten. Slechts enkele velden zijn verplicht (gemarkeerd met *) — de rest helpt haar om interessanter te zijn, maar u kunt altijd later terugkomen.",
    "reviewTitle": "Controleer uw antwoorden",
    "reviewSub": "Bekijk alles hieronder en verbeter wat niet klopt. U kunt later altijd terugkomen en gegevens aanpassen vanuit de portal."
  },
  "validationSummaryTitle": "Er ontbreken nog enkele verplichte velden",
  "validation": {
    "firstNameFamily": "Wat is zijn of haar voornaam?",
    "firstNameSelf": "Wat is uw voornaam?",
    "birthdayMissingFamily": "De verjaardag is nodig zodat Lena op de juiste dag kan feliciteren.",
    "birthdayMissingSelf": "Uw verjaardag is nodig zodat Lena u op de juiste dag kan feliciteren.",
    "birthdayAdultFamily": "Amigo Mio is gemaakt voor volwassenen — controleer de verjaardag.",
    "birthdayAdultSelf": "U moet minstens 18 jaar zijn om een account aan te maken.",
    "languageFamily": "Kies een taal zodat de metgezel met hem of haar kan praten.",
    "languageSelf": "Kies een taal zodat Lena met u kan praten.",
    "interestsFamily": "Voeg minstens 3 interesses toe (u heeft er {count}). Verdeel ze over de categorieën hieronder — dit is waarover de metgezel zal praten.",
    "interestsSelf": "Voeg minstens 3 interesses toe (u heeft er {count}). Verdeel ze over de categorieën hieronder — dit is waarover Lena met u zal praten.",
    "familyFamily": "Voeg minstens één familielid toe — namen en relaties geven houvast aan het gesprek.",
    "familySelf": "Voeg minstens één familielid of vriend toe — namen en relaties geven houvast aan het gesprek."
  },
  "personal": {
    "section": "Persoonlijk",
    "firstName": "Voornaam",
    "lastName": "Achternaam",
    "preferredNameFamily": "Roepnaam (hoe hij of zij graag genoemd wordt)",
    "preferredNameSelf": "Roepnaam (hoe wilt u genoemd worden)",
    "birthday": "Verjaardag",
    "birthdayHelp": "Helpt de metgezel om muziek, films en onderwerpen uit de juiste tijd te kiezen — en laat Lena op de juiste dag feliciteren met de verjaardag.",
    "under18Family": "Amigo Mio is gemaakt voor volwassen gebruikers — controleer de geboortedatum. Het account wordt pas aangemaakt wanneer dit een volwassene aangeeft.",
    "under18Self": "U moet minstens 18 jaar zijn om een account aan te maken. Het account wordt pas aangemaakt wanneer de geboortedatum een volwassene aangeeft.",
    "language": "Taal",
    "companionName": "Naam van de metgezel",
    "companionNamePlaceholder": "Lena"
  },
  "location": {
    "section": "Locatie",
    "cityCountry": "In welke stad en welk land woont hij of zij nu?",
    "cityCountryPlaceholder": "bijv. Antwerpen, België",
    "timezone": "Tijdzone",
    "timezoneHelp": "We gebruiken dit om op het juiste moment van de dag te groeten en om herinneringsoproepen binnen hun dag te plannen. Gedetecteerd via uw browser — pas het aan als hij of zij ergens anders woont.",
    "otherPlaces": "Andere plaatsen waar hij of zij gewoond heeft"
  },
  "interests": {
    "section": "Interesses",
    "sectionHint": "(minstens 3 verdeeld over de categorieën hieronder — waarover de metgezel zal praten)",
    "help": "Categorieën helpen de metgezel te weten wat voor soort interesse elk is — wanneer muziek te spelen, een film te tonen of over sport te praten. U hoeft niet elke categorie in te vullen — alleen die passen.",
    "minCount": "{count} / 3 minimum",
    "topicsToAvoid": "Onderwerpen om te vermijden",
    "buckets": {
      "music_artists": {
        "label": "Favoriete zangers / bands",
        "placeholder": "bijv. Queen, Frank Sinatra"
      },
      "music_genres": {
        "label": "Muziekstijlen / genres",
        "placeholder": "bijv. klassiek, jazz uit de jaren 50"
      },
      "sports_interests": {
        "label": "Sporten / ploegen",
        "placeholder": "bijv. Club Brugge, tennis op Wimbledon"
      },
      "screen_interests": {
        "label": "Films / tv / acteurs",
        "placeholder": "bijv. Charlie Chaplin, Casablanca"
      },
      "places_interests": {
        "label": "Favoriete plaatsen",
        "placeholder": "bijv. de Ardennen, Barcelona"
      },
      "hobbies": {
        "label": "Hobby's",
        "placeholder": "bijv. tuinieren, kruiswoordraadsels"
      },
      "other_interests": {
        "label": "Andere interesses",
        "placeholder": "alles wat verder de moeite waard is om te weten"
      }
    }
  },
  "family": {
    "section": "Familie",
    "sectionHint": "(minstens één — namen + relaties geven houvast aan elk gesprek)",
    "empty": "Nog geen familieleden toegevoegd. Voeg er minstens één toe — namen + relaties geven houvast aan elk gesprek.",
    "name": "Naam",
    "namePlaceholder": "bijv. Maria",
    "relation": "Relatie",
    "whose": "Van wie?",
    "whoseHintFamily": "(standaard uw dierbare)",
    "whoseHintSelf": "(standaard uzelf)",
    "whosePlaceholderFamily": "Uw dierbare",
    "whosePlaceholderSelf": "U",
    "stillWithUs": "Nog in leven?",
    "statusAlive": "Ja",
    "statusDeceased": "Overleden",
    "notes": "Notities",
    "notesHint": "(optioneel — alles wat de metgezel zou moeten weten)",
    "notesPlaceholder": "bijv. woont in Antwerpen, belt elke zondag",
    "addBtn": "+ Familielid toevoegen",
    "readbackAlive": "{name}, die nog in leven is, is een {relation} van {relTo}.",
    "readbackDeceased": "{name}, die overleden is, was een {relation} van {relTo}."
  },
  "friends": {
    "section": "Vrienden",
    "sectionHint": "(optioneel — oude vrienden naar wie Lena kan vragen)",
    "empty": "Nog geen vrienden toegevoegd. Optioneel, maar Lena vraagt graag naar oude vrienden.",
    "name": "Naam",
    "namePlaceholder": "bijv. Jan Boer",
    "status": "Status",
    "statusInContact": "Nog contact",
    "statusLostTouch": "Contact verloren",
    "statusDeceased": "Overleden",
    "location": "Locatie",
    "locationPlaceholder": "Waar hij of zij nu woont",
    "howMet": "Hoe ze elkaar leerden kennen",
    "howMetPlaceholder": "bijv. schoolvrienden, buren, collega's",
    "sharedInterests": "Gedeelde interesses",
    "sharedInterestsPlaceholder": "Wat ze samen deden / waarover ze praatten",
    "addBtn": "+ Vriend toevoegen"
  },
  "pets": {
    "section": "Huisdieren",
    "sectionHint": "(optioneel — huidige en vroegere)",
    "empty": "Nog geen huisdieren toegevoegd. Optioneel, maar huisdieren horen bij de warmste dingen waarnaar Lena kan vragen.",
    "name": "Naam",
    "namePlaceholder": "bijv. Bobby",
    "species": "Soort",
    "speciesPlaceholder": "Hond, kat, parkiet…",
    "status": "Status",
    "statusCurrent": "Huidig",
    "statusPast": "Vroeger",
    "note": "Notitie",
    "notePlaceholder": "bijv. uit het asiel gehaald, dol op wandelingen",
    "addBtn": "+ Huisdier toevoegen"
  },
  "faith": {
    "section": "Geloof / spiritualiteit",
    "tradition": "Traditie",
    "notSpecified": "(niet opgegeven)",
    "otherSpecify": "Andere (specificeer)…",
    "traditionCustomPlaceholder": "Typ de traditie",
    "traditionHelp": "Leeg / \"geen\" / \"niet gelovig\" betekent allemaal dat Lena geen verwijzingen naar geloof zal maken.",
    "importance": "Hoe belangrijk is het in het dagelijks leven?",
    "importanceCentral": "Centraal in het dagelijks leven",
    "importanceCultural": "Alleen cultureel / feestdagen",
    "importanceNotReally": "Niet echt deel van het dagelijks leven",
    "practicesFamily": "Gebruiken & tradities die zij volgt",
    "practicesSelf": "Gebruiken & tradities die u volgt",
    "practicesPlaceholderFamily": "bijv. gaat met haar dochter naar de nachtmis; steekt kaarsen aan met Allerheiligen.",
    "practicesPlaceholderSelf": "bijv. u gaat met uw dochter naar de nachtmis; steekt kaarsen aan met Allerheiligen."
  },
  "childhood": {
    "section": "Kindertijd",
    "growUpFamily": "Waar hij of zij opgroeide — familie, thuis, alles wat hem of haar gevormd heeft",
    "growUpSelf": "Waar u opgroeide — familie, thuis, alles wat u gevormd heeft",
    "growUpPlaceholderFamily": "bijv. oudste van vier in Gent — vader mijnwerker, zomers aan zee in Oostende, leerde bakken bij haar oma. Sla over als dit geen fijn onderwerp is.",
    "growUpPlaceholderSelf": "bijv. oudste van vier in Gent — vader mijnwerker, zomers aan zee in Oostende, u leerde bakken bij uw oma. Sla over als dit geen fijn onderwerp is.",
    "sensitiveNotes": "Gevoelige notities",
    "sensitiveBadge": "privé",
    "sensitivePlaceholder": "Alles wat Lena zou moeten weten maar NOOIT ter sprake mag brengen — bijv. een strenge ouder, een verlies, een moeilijke periode.",
    "sensitiveHelp": "🔒 Versleuteld, wordt nooit aan u getoond of hardop voorgelezen. Lena krijgt dit alleen als richtlijn voor wat te vermijden."
  },
  "working": {
    "section": "Werkend leven",
    "profession": "Beroep of functie",
    "professionPlaceholder": "bijv. onderwijzeres",
    "employer": "Werkgever",
    "employerPlaceholder": "bijv. Sint-Jozefschool, Gent",
    "industry": "Sector",
    "industryPlaceholder": "bijv. Onderwijs",
    "yearsActive": "Jaren actief",
    "yearsActivePlaceholder": "bijv. 1962 – 1998",
    "proudFamily": "Waar is zij het meest trots op?",
    "proudSelf": "Waar bent u het meest trots op?",
    "proudPlaceholderFamily": "bijv. heeft honderden kinderen leren lezen; krijgt nog steeds brieven van oud-leerlingen.",
    "proudPlaceholderSelf": "bijv. heeft honderden kinderen leren lezen; krijgt nog steeds brieven van oud-leerlingen."
  },
  "memories": {
    "section": "Mooie herinneringen & levensgebeurtenissen",
    "label": "Bijzondere momenten of herinneringen",
    "placeholder": "Gedenkwaardige gebeurtenissen, reizen, mijlpalen…"
  },
  "health": {
    "section": "Gezondheids- & geheugennotities",
    "ongoing": "Aanhoudende gezondheidsklachten",
    "ongoingHelp": "Langdurige aandoeningen waarvan de metgezel op de hoogte moet zijn voor tempo en gevoeligheid (diabetes, artrose, gehoorverlies…). Ze gebruikt deze stilzwijgend — ze brengt ze niet als onderwerp ter sprake.",
    "chronicEmpty": "Nog geen aanhoudende gezondheidsklachten toegevoegd. Optioneel — voeg alleen dingen toe die niet zullen veranderen (diabetes, artrose, gehoorverlies, enz.). De metgezel brengt ze niet rechtstreeks ter sprake.",
    "chronicPlaceholder": "bijv. type 2-diabetes sinds 2015, goed onder controle met medicatie",
    "addCondition": "+ Aandoening toevoegen",
    "notesPrivate": "Notities voor de metgezel (privé)",
    "notesPrivateHelp": "Gevoelige richtlijnen die ze moet kennen maar nooit ter sprake mag brengen (een moeilijk verleden, dingen om te vermijden). Blijft privé; wordt nooit herhaald.",
    "notesPrivatePlaceholder": "Alles wat de metgezel zou moeten weten om zich aan te passen…"
  },
  "saveButton": "Opslaan & naar de vervolgvragen",
  "followup": {
    "loadingTitle": "Bezig met het schrijven van vijf vragen over hem of haar…",
    "loadingSub": "Dit duurt meestal een paar seconden.",
    "emptyTitle": "Nog één laatste stap…",
    "emptyBody": "We konden de persoonlijke vragen op dit moment niet genereren{errorSuffix} — u kunt zonder hen afronden en Lena heeft nog steeds alles uit het formulier.",
    "continue": "Doorgaan",
    "headerTitle": "Een paar vragen om de metgezel als familie te laten voelen",
    "headerSubFamily": "Deze zijn persoonlijk voor uw dierbare — beantwoord wat u te binnen schiet, sla de rest over. Zelfs één mooi verhaal helpt de metgezel te klinken alsof ze hem of haar al jaren kent.",
    "headerSubSelf": "Dit zijn persoonlijke vragen voor u — beantwoord wat u te binnen schiet, sla de rest over. Zelfs één mooi verhaal helpt de metgezel te klinken alsof ze u al jaren kent.",
    "questionN": "Vraag {n}",
    "answerPlaceholder": "Uw antwoord (optioneel)",
    "showDifferent": "Andere vragen tonen",
    "generating": "Bezig met genereren…",
    "skip": "Overslaan — ik ben klaar",
    "saveFinish": "Opslaan & afronden",
    "errGenerate": "Kon geen vragen genereren",
    "errSave": "Kon antwoorden niet opslaan"
  },
  "allset": {
    "title": "Alles is klaar!",
    "subFamily": "{companion} is klaar om {name} te ontmoeten!",
    "subSelf": "{companion} is klaar om met u te praten!",
    "openTabletFamily": "Open dit op hun tablet",
    "openTabletSelf": "Open dit op uw tablet",
    "qrHelp": "Richt de camera van de tablet op de QR-code, of open een browser en typ het adres hierboven. De tablet wordt automatisch gekoppeld.",
    "reassurance": "Heeft u de tablet nu niet bij de hand? Geen zorgen — u kunt deze link altijd opnieuw opvragen via uw portal.",
    "goPortal": "Ga naar uw portal"
  },
  "toastLoadError": "Kon de controlegegevens niet laden",
  "saveFailed": "Opslaan mislukt"
};

const de = {
  "loading": "Ihre Antworten werden geladen…",
  "common": {
    "optional": "(optional)",
    "add": "Hinzufügen",
    "addItem": "+ Hinzufügen",
    "remove": "Entfernen",
    "saving": "Wird gespeichert…"
  },
  "header": {
    "newTitleFamily": "Erzählen Sie uns von Ihrem Angehörigen",
    "newTitleSelf": "Erzählen Sie uns von sich",
    "newSubFamily": "Tragen Sie die Grundlagen ein, damit der Begleiter weiß, wie er mit ihm sprechen soll. Nur wenige Felder sind erforderlich (mit * markiert) — der Rest hilft dem Begleiter, interessanter zu sein, aber Sie können jederzeit später zurückkommen.",
    "newSubSelf": "Tragen Sie die Grundlagen ein, damit Lena weiß, wie sie mit Ihnen sprechen soll. Nur wenige Felder sind erforderlich (mit * markiert) — der Rest hilft ihr, interessanter zu sein, aber Sie können jederzeit später zurückkommen.",
    "reviewTitle": "Überprüfen Sie Ihre Antworten",
    "reviewSub": "Prüfen Sie alles unten und korrigieren Sie, was nicht stimmt. Sie können jederzeit zurückkommen und später im Portal bearbeiten."
  },
  "validationSummaryTitle": "Einige Pflichtfelder fehlen noch",
  "validation": {
    "firstNameFamily": "Wie lautet sein Vorname?",
    "firstNameSelf": "Wie lautet Ihr Vorname?",
    "birthdayMissingFamily": "Der Geburtstag wird benötigt, damit Lena am richtigen Tag gratulieren kann.",
    "birthdayMissingSelf": "Ihr Geburtstag wird benötigt, damit Lena Ihnen am richtigen Tag gratulieren kann.",
    "birthdayAdultFamily": "Amigo Mio ist für Erwachsene gemacht — bitte überprüfen Sie den Geburtstag.",
    "birthdayAdultSelf": "Sie müssen mindestens 18 Jahre alt sein, um ein Konto zu erstellen.",
    "languageFamily": "Wählen Sie eine Sprache, damit der Begleiter mit ihm sprechen kann.",
    "languageSelf": "Wählen Sie eine Sprache, damit Lena mit Ihnen sprechen kann.",
    "interestsFamily": "Bitte fügen Sie mindestens 3 Interessen hinzu (Sie haben {count}). Verteilen Sie sie auf die Kategorien unten — darüber wird der Begleiter sprechen.",
    "interestsSelf": "Bitte fügen Sie mindestens 3 Interessen hinzu (Sie haben {count}). Verteilen Sie sie auf die Kategorien unten — darüber wird Lena mit Ihnen plaudern.",
    "familyFamily": "Fügen Sie mindestens ein Familienmitglied hinzu — Namen und Beziehungen geben dem Gespräch Halt.",
    "familySelf": "Fügen Sie mindestens ein Familienmitglied oder einen Freund hinzu — Namen und Beziehungen geben dem Gespräch Halt."
  },
  "personal": {
    "section": "Persönliches",
    "firstName": "Vorname",
    "lastName": "Nachname",
    "preferredNameFamily": "Rufname (wie er gerne genannt wird)",
    "preferredNameSelf": "Rufname (wie möchten Sie genannt werden)",
    "birthday": "Geburtstag",
    "birthdayHelp": "Hilft dem Begleiter, zeitlich passende Musik, Filme und Themen zu wählen — und lässt Lena am richtigen Tag zum Geburtstag gratulieren.",
    "under18Family": "Amigo Mio ist für erwachsene Nutzer gemacht — bitte überprüfen Sie das Geburtsdatum. Das Konto wird erst eingerichtet, wenn dies einen Erwachsenen zeigt.",
    "under18Self": "Sie müssen mindestens 18 Jahre alt sein, um ein Konto zu erstellen. Das Konto wird erst eingerichtet, wenn das Geburtsdatum einen Erwachsenen zeigt.",
    "language": "Sprache",
    "companionName": "Name des Begleiters",
    "companionNamePlaceholder": "Lena"
  },
  "location": {
    "section": "Wohnort",
    "cityCountry": "In welcher Stadt und welchem Land lebt er jetzt?",
    "cityCountryPlaceholder": "z. B. Hamburg, Deutschland",
    "timezone": "Zeitzone",
    "timezoneHelp": "Wir nutzen dies, um ihn zur richtigen Tageszeit zu begrüßen und Erinnerungsanrufe in seinen Tagesablauf einzuplanen. Aus Ihrem Browser erkannt — ändern Sie es, wenn er anderswo lebt.",
    "otherPlaces": "Andere Orte, an denen er gelebt hat"
  },
  "interests": {
    "section": "Interessen",
    "sectionHint": "(mindestens 3 über die Kategorien unten — worüber der Begleiter sprechen wird)",
    "help": "Die Kategorien helfen dem Begleiter zu erkennen, um welche Art von Interesse es sich jeweils handelt — wann Musik abgespielt, ein Film gezeigt oder über Sport gesprochen werden soll. Sie müssen nicht jede Kategorie ausfüllen — nur die, die passen.",
    "minCount": "{count} / 3 mindestens",
    "topicsToAvoid": "Themen, die zu vermeiden sind",
    "buckets": {
      "music_artists": {
        "label": "Lieblingssänger / -bands",
        "placeholder": "z. B. Queen, Frank Sinatra"
      },
      "music_genres": {
        "label": "Musikstile / Genres",
        "placeholder": "z. B. Klassik, Jazz der 1950er"
      },
      "sports_interests": {
        "label": "Sport / Mannschaften",
        "placeholder": "z. B. FC Bayern, Wimbledon-Tennis"
      },
      "screen_interests": {
        "label": "Filme / TV / Schauspieler",
        "placeholder": "z. B. Charlie Chaplin, Casablanca"
      },
      "places_interests": {
        "label": "Lieblingsorte",
        "placeholder": "z. B. Schwarzwald, Barcelona"
      },
      "hobbies": {
        "label": "Hobbys",
        "placeholder": "z. B. Gärtnern, Kreuzworträtsel"
      },
      "other_interests": {
        "label": "Weitere Interessen",
        "placeholder": "alles Weitere, das wissenswert ist"
      }
    }
  },
  "family": {
    "section": "Familie",
    "sectionHint": "(mindestens eines — Namen + Beziehungen geben jedem Gespräch Halt)",
    "empty": "Noch keine Familienmitglieder hinzugefügt. Fügen Sie mindestens eines hinzu — Namen + Beziehungen geben jedem Gespräch Halt.",
    "name": "Name",
    "namePlaceholder": "z. B. Maria",
    "relation": "Beziehung",
    "whose": "Wessen?",
    "whoseHintFamily": "(standardmäßig Ihr Angehöriger)",
    "whoseHintSelf": "(standardmäßig Sie)",
    "whosePlaceholderFamily": "Ihr Angehöriger",
    "whosePlaceholderSelf": "Sie",
    "stillWithUs": "Noch unter uns?",
    "statusAlive": "Ja",
    "statusDeceased": "Verstorben",
    "notes": "Notizen",
    "notesHint": "(optional — alles, was der Begleiter wissen sollte)",
    "notesPlaceholder": "z. B. lebt in Barcelona, ruft jeden Sonntag an",
    "addBtn": "+ Familienmitglied hinzufügen",
    "readbackAlive": "{name}, der noch unter uns ist, ist {relation} von {relTo}.",
    "readbackDeceased": "{name}, der verstorben ist, war {relation} von {relTo}."
  },
  "friends": {
    "section": "Freunde",
    "sectionHint": "(optional — alte Freunde, nach denen Lena fragen kann)",
    "empty": "Noch keine Freunde hinzugefügt. Optional, aber Lena fragt gerne nach alten Freunden.",
    "name": "Name",
    "namePlaceholder": "z. B. Jan Bauer",
    "status": "Status",
    "statusInContact": "Noch in Kontakt",
    "statusLostTouch": "Kontakt verloren",
    "statusDeceased": "Verstorben",
    "location": "Wohnort",
    "locationPlaceholder": "Wo er jetzt lebt",
    "howMet": "Wie sie sich kennengelernt haben",
    "howMetPlaceholder": "z. B. Schulfreunde, Nachbarn, Kollegen",
    "sharedInterests": "Gemeinsame Interessen",
    "sharedInterestsPlaceholder": "Was sie früher zusammen unternommen / besprochen haben",
    "addBtn": "+ Freund hinzufügen"
  },
  "pets": {
    "section": "Haustiere",
    "sectionHint": "(optional — aktuelle und frühere)",
    "empty": "Noch keine Haustiere hinzugefügt. Optional, aber Haustiere gehören zu den schönsten Dingen, nach denen Lena fragen kann.",
    "name": "Name",
    "namePlaceholder": "z. B. Bello",
    "species": "Art",
    "speciesPlaceholder": "Hund, Katze, Wellensittich…",
    "status": "Status",
    "statusCurrent": "Aktuell",
    "statusPast": "Früher",
    "note": "Notiz",
    "notePlaceholder": "z. B. aus dem Tierheim, liebt Spaziergänge",
    "addBtn": "+ Haustier hinzufügen"
  },
  "faith": {
    "section": "Glaube / Spiritualität",
    "tradition": "Tradition",
    "notSpecified": "(nicht angegeben)",
    "otherSpecify": "Andere (angeben)…",
    "traditionCustomPlaceholder": "Tradition eingeben",
    "traditionHelp": "Leer / \"keine\" / \"nicht religiös\" bedeuten alle, dass Lena keine Bezüge zum Glauben herstellt.",
    "importance": "Wie wichtig ist es im Alltag?",
    "importanceCentral": "Zentral im täglichen Leben",
    "importanceCultural": "Nur kulturell / an Feiertagen",
    "importanceNotReally": "Nicht wirklich Teil des Alltags",
    "practicesFamily": "Bräuche & Traditionen, die sie pflegt",
    "practicesSelf": "Bräuche & Traditionen, die Sie pflegen",
    "practicesPlaceholderFamily": "z. B. geht mit ihrer Tochter zur Christmette; zündet zu Allerheiligen Kerzen an.",
    "practicesPlaceholderSelf": "z. B. Sie gehen mit Ihrer Tochter zur Christmette; zünden zu Allerheiligen Kerzen an."
  },
  "childhood": {
    "section": "Kindheit",
    "growUpFamily": "Wo er aufgewachsen ist — Familie, Zuhause, alles, was ihn geprägt hat",
    "growUpSelf": "Wo Sie aufgewachsen sind — Familie, Zuhause, alles, was Sie geprägt hat",
    "growUpPlaceholderFamily": "z. B. Älteste von vier in Bremen — Vater war Hafenarbeiter, Sommer an der Nordsee, lernte bei ihrer Oma das Backen. Überspringen, wenn kein schönes Thema.",
    "growUpPlaceholderSelf": "z. B. Älteste von vier in Bremen — Vater war Hafenarbeiter, Sommer an der Nordsee, Sie lernten bei Ihrer Oma das Backen. Überspringen, wenn kein schönes Thema.",
    "sensitiveNotes": "Sensible Notizen",
    "sensitiveBadge": "privat",
    "sensitivePlaceholder": "Alles, was Lena wissen sollte, aber NIEMALS ansprechen darf — z. B. ein strenger Elternteil, ein Verlust, eine schwere Zeit.",
    "sensitiveHelp": "🔒 Verschlüsselt, wird Ihnen nie zurückgezeigt oder vorgelesen. Lena erhält dies nur als Hinweis darauf, was zu vermeiden ist."
  },
  "working": {
    "section": "Berufsleben",
    "profession": "Beruf oder Tätigkeit",
    "professionPlaceholder": "z. B. Grundschullehrerin",
    "employer": "Arbeitgeber",
    "employerPlaceholder": "z. B. Grundschule St. Marien, Bremen",
    "industry": "Branche",
    "industryPlaceholder": "z. B. Bildung",
    "yearsActive": "Aktive Jahre",
    "yearsActivePlaceholder": "z. B. 1962 – 1998",
    "proudFamily": "Worauf ist sie am meisten stolz?",
    "proudSelf": "Worauf sind Sie am meisten stolz?",
    "proudPlaceholderFamily": "z. B. hat Hunderten von Kindern das Lesen beigebracht; bekommt noch heute Briefe von ehemaligen Schülern.",
    "proudPlaceholderSelf": "z. B. haben Hunderten von Kindern das Lesen beigebracht; bekommen noch heute Briefe von ehemaligen Schülern."
  },
  "memories": {
    "section": "Schöne Erinnerungen & Lebensereignisse",
    "label": "Besondere Momente oder Erinnerungen",
    "placeholder": "Denkwürdige Ereignisse, Reisen, Meilensteine…"
  },
  "health": {
    "section": "Gesundheits- & Gedächtnisnotizen",
    "ongoing": "Anhaltende Gesundheitsbeschwerden",
    "ongoingHelp": "Langjährige Beschwerden, die der Begleiter wegen Tempo und Feingefühl kennen sollte (Diabetes, Arthrose, Hörverlust…). Sie nutzt diese stillschweigend — sie spricht sie nicht als Thema an.",
    "chronicEmpty": "Noch keine anhaltenden Gesundheitsbeschwerden hinzugefügt. Optional — fügen Sie nur Dinge hinzu, die sich nicht ändern (Diabetes, Arthrose, Hörverlust usw.). Der Begleiter spricht sie nicht direkt an.",
    "chronicPlaceholder": "z. B. Typ-2-Diabetes seit 2015, mit Medikamenten gut eingestellt",
    "addCondition": "+ Beschwerde hinzufügen",
    "notesPrivate": "Notizen für den Begleiter (privat)",
    "notesPrivateHelp": "Sensible Hinweise, die sie kennen, aber nie ansprechen sollte (eine schwierige Vergangenheit, zu vermeidende Dinge). Bleibt privat; wird nie zurückgespiegelt.",
    "notesPrivatePlaceholder": "Alles, was der Begleiter wissen sollte, um sich anzupassen…"
  },
  "saveButton": "Speichern & weiter zu den Anschlussfragen",
  "followup": {
    "loadingTitle": "Fünf Fragen über ihn werden geschrieben…",
    "loadingSub": "Das dauert normalerweise nur ein paar Sekunden.",
    "emptyTitle": "Ein letzter Schritt…",
    "emptyBody": "Wir konnten die persönlichen Fragen gerade nicht erstellen{errorSuffix} — Sie können ohne sie abschließen, und Lena hat trotzdem alles aus dem Formular.",
    "continue": "Weiter",
    "headerTitle": "Ein paar Fragen, damit sich der Begleiter wie Familie anfühlt",
    "headerSubFamily": "Diese sind persönlich für Ihren Angehörigen — beantworten Sie, was Ihnen einfällt, und überspringen Sie den Rest. Schon eine gute Geschichte hilft dem Begleiter zu klingen, als kenne er ihn seit Jahren.",
    "headerSubSelf": "Dies sind persönliche Fragen für Sie — beantworten Sie, was Ihnen einfällt, und überspringen Sie den Rest. Schon eine gute Geschichte hilft dem Begleiter zu klingen, als kenne er Sie seit Jahren.",
    "questionN": "Frage {n}",
    "answerPlaceholder": "Ihre Antwort (optional)",
    "showDifferent": "Andere Fragen anzeigen",
    "generating": "Wird erstellt…",
    "skip": "Überspringen — ich bin fertig",
    "saveFinish": "Speichern & abschließen",
    "errGenerate": "Fragen konnten nicht erstellt werden",
    "errSave": "Antworten konnten nicht gespeichert werden"
  },
  "allset": {
    "title": "Alles bereit!",
    "subFamily": "{companion} freut sich darauf, {name} kennenzulernen!",
    "subSelf": "{companion} freut sich darauf, mit Ihnen zu plaudern!",
    "openTabletFamily": "Öffnen Sie dies auf seinem Tablet",
    "openTabletSelf": "Öffnen Sie dies auf Ihrem Tablet",
    "qrHelp": "Richten Sie die Kamera des Tablets auf den QR-Code oder öffnen Sie einen Browser und geben Sie die Adresse oben ein. Das Tablet wird automatisch gekoppelt.",
    "reassurance": "Sie haben das Tablet gerade nicht zur Hand? Kein Problem — Sie können diesen Link jederzeit wieder über Ihr Portal erhalten.",
    "goPortal": "Zu Ihrem Portal"
  },
  "toastLoadError": "Überprüfungsdaten konnten nicht geladen werden",
  "saveFailed": "Speichern fehlgeschlagen"
};

const fr = {
  "loading": "Chargement de vos réponses…",
  "common": {
    "optional": "(facultatif)",
    "add": "Ajouter",
    "addItem": "+ Ajouter",
    "remove": "Supprimer",
    "saving": "Enregistrement…"
  },
  "header": {
    "newTitleFamily": "Parlez-nous de votre proche",
    "newTitleSelf": "Parlez-nous de vous",
    "newSubFamily": "Renseignez les informations de base pour que le compagnon sache comment lui parler. Seuls quelques champs sont obligatoires (marqués d'un *) — les autres aident le compagnon à se montrer plus intéressant, mais vous pourrez toujours y revenir plus tard.",
    "newSubSelf": "Renseignez les informations de base pour que Lena sache comment vous parler. Seuls quelques champs sont obligatoires (marqués d'un *) — les autres l'aident à se montrer plus intéressante, mais vous pourrez toujours y revenir plus tard.",
    "reviewTitle": "Vérifiez vos réponses",
    "reviewSub": "Vérifiez tout ce qui suit et corrigez ce qui ne va pas. Vous pourrez toujours revenir y apporter des modifications plus tard depuis le portail."
  },
  "validationSummaryTitle": "Quelques champs obligatoires sont manquants",
  "validation": {
    "firstNameFamily": "Quel est son prénom ?",
    "firstNameSelf": "Quel est votre prénom ?",
    "birthdayMissingFamily": "La date de naissance est nécessaire pour que Lena puisse lui souhaiter son anniversaire le bon jour.",
    "birthdayMissingSelf": "Votre date de naissance est nécessaire pour que Lena puisse vous souhaiter votre anniversaire le bon jour.",
    "birthdayAdultFamily": "Amigo Mio est conçu pour les adultes — veuillez vérifier la date de naissance.",
    "birthdayAdultSelf": "Vous devez avoir au moins 18 ans pour créer un compte.",
    "languageFamily": "Choisissez une langue pour que le compagnon puisse lui parler.",
    "languageSelf": "Choisissez une langue pour que Lena puisse vous parler.",
    "interestsFamily": "Veuillez ajouter au moins 3 centres d'intérêt (vous en avez {count}). Répartissez-les entre les catégories ci-dessous — ce sont les sujets dont le compagnon parlera.",
    "interestsSelf": "Veuillez ajouter au moins 3 centres d'intérêt (vous en avez {count}). Répartissez-les entre les catégories ci-dessous — ce sont les sujets dont Lena vous parlera.",
    "familyFamily": "Ajoutez au moins un membre de la famille — les noms et les liens de parenté ancrent la conversation.",
    "familySelf": "Ajoutez au moins un membre de la famille ou un ami — les noms et les liens ancrent la conversation."
  },
  "personal": {
    "section": "Informations personnelles",
    "firstName": "Prénom",
    "lastName": "Nom",
    "preferredNameFamily": "Nom d'usage (comment aime-t-il/elle être appelé(e))",
    "preferredNameSelf": "Nom d'usage (comment souhaitez-vous être appelé(e))",
    "birthday": "Date de naissance",
    "birthdayHelp": "Aide le compagnon à choisir une musique, des films et des sujets adaptés à son époque — et permet à Lena de lui souhaiter un joyeux anniversaire le bon jour.",
    "under18Family": "Amigo Mio est conçu pour des utilisateurs adultes — veuillez vérifier la date de naissance. Le compte ne sera pas créé tant que celle-ci n'indiquera pas une personne majeure.",
    "under18Self": "Vous devez avoir au moins 18 ans pour créer un compte. Le compte ne sera pas créé tant que la date de naissance n'indiquera pas une personne majeure.",
    "language": "Langue",
    "companionName": "Nom du compagnon",
    "companionNamePlaceholder": "Lena"
  },
  "location": {
    "section": "Lieu",
    "cityCountry": "Dans quelle ville et quel pays vit-il/elle actuellement ?",
    "cityCountryPlaceholder": "p. ex. Lyon, France",
    "timezone": "Fuseau horaire",
    "timezoneHelp": "Nous l'utilisons pour le/la saluer au bon moment de la journée et pour programmer les appels de rappel dans sa journée. Détecté à partir de votre navigateur — modifiez-le s'il/elle vit ailleurs.",
    "otherPlaces": "Autres lieux où il/elle a vécu"
  },
  "interests": {
    "section": "Centres d'intérêt",
    "sectionHint": "(au moins 3 répartis entre les catégories ci-dessous — les sujets dont le compagnon parlera)",
    "help": "Les catégories aident le compagnon à savoir de quel type d'intérêt il s'agit — quand passer de la musique, montrer un film ou parler de sport. Vous n'avez pas besoin de remplir chaque catégorie — seulement celles qui conviennent.",
    "minCount": "{count} / 3 minimum",
    "topicsToAvoid": "Sujets à éviter",
    "buckets": {
      "music_artists": {
        "label": "Chanteurs / groupes préférés",
        "placeholder": "p. ex. Édith Piaf, Jacques Brel"
      },
      "music_genres": {
        "label": "Styles / genres de musique",
        "placeholder": "p. ex. musique classique, jazz des années 1950"
      },
      "sports_interests": {
        "label": "Sports / équipes",
        "placeholder": "p. ex. l'Olympique de Marseille, le tennis à Roland-Garros"
      },
      "screen_interests": {
        "label": "Films / séries / acteurs",
        "placeholder": "p. ex. Louis de Funès, Les Tontons flingueurs"
      },
      "places_interests": {
        "label": "Lieux préférés",
        "placeholder": "p. ex. la Bretagne, Nice"
      },
      "hobbies": {
        "label": "Loisirs",
        "placeholder": "p. ex. le jardinage, les mots croisés"
      },
      "other_interests": {
        "label": "Autres centres d'intérêt",
        "placeholder": "tout ce qui mérite d'être connu"
      }
    }
  },
  "family": {
    "section": "Famille",
    "sectionHint": "(au moins un — les noms et les liens ancrent chaque conversation)",
    "empty": "Aucun membre de la famille ajouté pour l'instant. Ajoutez-en au moins un — les noms et les liens ancrent chaque conversation.",
    "name": "Nom",
    "namePlaceholder": "p. ex. Marie",
    "relation": "Lien de parenté",
    "whose": "De qui ?",
    "whoseHintFamily": "(par défaut, votre proche)",
    "whoseHintSelf": "(par défaut, vous)",
    "whosePlaceholderFamily": "Votre proche",
    "whosePlaceholderSelf": "Vous",
    "stillWithUs": "Toujours parmi nous ?",
    "statusAlive": "Oui",
    "statusDeceased": "Décédé(e)",
    "notes": "Notes",
    "notesHint": "(facultatif — tout ce que le compagnon devrait savoir)",
    "notesPlaceholder": "p. ex. vit à Lyon, appelle tous les dimanches",
    "addBtn": "+ Ajouter un membre de la famille",
    "readbackAlive": "{name}, qui est toujours parmi nous, est {relation} de {relTo}.",
    "readbackDeceased": "{name}, qui est décédé(e), était {relation} de {relTo}."
  },
  "friends": {
    "section": "Amis",
    "sectionHint": "(facultatif — de vieux amis dont Lena peut prendre des nouvelles)",
    "empty": "Aucun ami ajouté pour l'instant. C'est facultatif, mais Lena adore prendre des nouvelles des vieux amis.",
    "name": "Nom",
    "namePlaceholder": "p. ex. Jean Dupont",
    "status": "Statut",
    "statusInContact": "Toujours en contact",
    "statusLostTouch": "Perdu de vue",
    "statusDeceased": "Décédé(e)",
    "location": "Lieu",
    "locationPlaceholder": "Où il/elle vit maintenant",
    "howMet": "Comment ils se sont rencontrés",
    "howMetPlaceholder": "p. ex. amis d'école, voisins, collègues",
    "sharedInterests": "Centres d'intérêt communs",
    "sharedInterestsPlaceholder": "Ce qu'ils faisaient / ce dont ils parlaient ensemble",
    "addBtn": "+ Ajouter un ami"
  },
  "pets": {
    "section": "Animaux de compagnie",
    "sectionHint": "(facultatif — actuels et passés)",
    "empty": "Aucun animal ajouté pour l'instant. C'est facultatif, mais les animaux sont parmi les sujets les plus chaleureux que Lena puisse aborder.",
    "name": "Nom",
    "namePlaceholder": "p. ex. Rex",
    "species": "Espèce",
    "speciesPlaceholder": "Chien, chat, perruche…",
    "status": "Statut",
    "statusCurrent": "Actuel",
    "statusPast": "Passé",
    "note": "Note",
    "notePlaceholder": "p. ex. recueilli au refuge, adore les promenades",
    "addBtn": "+ Ajouter un animal"
  },
  "faith": {
    "section": "Foi / spiritualité",
    "tradition": "Tradition",
    "notSpecified": "(non précisé)",
    "otherSpecify": "Autre (à préciser)…",
    "traditionCustomPlaceholder": "Saisissez la tradition",
    "traditionHelp": "Laisser vide / « aucune » / « non religieux » signifie que Lena ne fera aucune référence à la foi.",
    "importance": "Quelle importance au quotidien ?",
    "importanceCentral": "Centrale dans la vie quotidienne",
    "importanceCultural": "Culturelle / fêtes uniquement",
    "importanceNotReally": "Pas vraiment présente au quotidien",
    "practicesFamily": "Pratiques et traditions qu'elle perpétue",
    "practicesSelf": "Pratiques et traditions que vous perpétuez",
    "practicesPlaceholderFamily": "p. ex. elle assiste à la messe de Noël avec sa fille ; allume des bougies pour la Toussaint.",
    "practicesPlaceholderSelf": "p. ex. vous assistez à la messe de Noël avec votre fille ; allumez des bougies pour la Toussaint."
  },
  "childhood": {
    "section": "Enfance",
    "growUpFamily": "Où il/elle a grandi — la famille, le foyer, tout ce qui l'a façonné(e)",
    "growUpSelf": "Où vous avez grandi — la famille, le foyer, tout ce qui vous a façonné(e)",
    "growUpPlaceholderFamily": "p. ex. aînée de quatre enfants à Lille — père mineur, étés à Deauville, a appris à cuisiner avec sa grand-mère. À passer si ce n'est pas un sujet heureux.",
    "growUpPlaceholderSelf": "p. ex. aîné(e) de quatre enfants à Lille — père mineur, étés à Deauville, vous avez appris à cuisiner avec votre grand-mère. À passer si ce n'est pas un sujet heureux.",
    "sensitiveNotes": "Notes sensibles",
    "sensitiveBadge": "privé",
    "sensitivePlaceholder": "Tout ce que Lena devrait savoir mais ne devrait JAMAIS aborder — p. ex. un parent strict, un deuil, une période difficile.",
    "sensitiveHelp": "🔒 Chiffré, jamais réaffiché ni lu à voix haute. Lena ne le reçoit qu'à titre indicatif, pour savoir ce qu'il faut éviter."
  },
  "working": {
    "section": "Vie professionnelle",
    "profession": "Profession ou fonction",
    "professionPlaceholder": "p. ex. institutrice",
    "employer": "Employeur",
    "employerPlaceholder": "p. ex. école Saint-Joseph, Lille",
    "industry": "Secteur d'activité",
    "industryPlaceholder": "p. ex. Éducation",
    "yearsActive": "Années d'activité",
    "yearsActivePlaceholder": "p. ex. 1962 – 1998",
    "proudFamily": "De quoi est-elle le plus fière ?",
    "proudSelf": "De quoi êtes-vous le plus fier/fière ?",
    "proudPlaceholderFamily": "p. ex. a donné le goût de la lecture à des centaines d'enfants ; reçoit encore des lettres d'anciens élèves.",
    "proudPlaceholderSelf": "p. ex. avez donné le goût de la lecture à des centaines d'enfants ; recevez encore des lettres d'anciens élèves."
  },
  "memories": {
    "section": "Souvenirs heureux et moments de vie",
    "label": "Moments ou souvenirs particuliers",
    "placeholder": "Événements mémorables, voyages, grandes étapes…"
  },
  "health": {
    "section": "Notes de santé et de mémoire",
    "ongoing": "Problèmes de santé persistants",
    "ongoingHelp": "Affections de longue date que le compagnon devrait connaître pour adapter son rythme et sa sensibilité (diabète, arthrose, perte d'audition…). Elle s'en sert discrètement — elle n'en fera pas un sujet de conversation.",
    "chronicEmpty": "Aucun problème de santé persistant ajouté pour l'instant. Facultatif — n'ajoutez que ce qui ne changera pas (diabète, arthrose, perte d'audition, etc.). Le compagnon n'abordera pas ces sujets directement.",
    "chronicPlaceholder": "p. ex. diabète de type 2 depuis 2015, bien contrôlé par traitement",
    "addCondition": "+ Ajouter un problème de santé",
    "notesPrivate": "Notes pour le compagnon (privées)",
    "notesPrivateHelp": "Recommandations sensibles qu'elle devrait connaître mais ne jamais évoquer (un passé difficile, des sujets à éviter). Restent privées ; jamais répétées.",
    "notesPrivatePlaceholder": "Tout ce que le compagnon devrait savoir pour s'adapter…"
  },
  "saveButton": "Enregistrer et passer aux questions de suivi",
  "followup": {
    "loadingTitle": "Rédaction de cinq questions à son sujet…",
    "loadingSub": "Cela prend généralement quelques secondes.",
    "emptyTitle": "Une dernière étape…",
    "emptyBody": "Nous n'avons pas pu générer les questions personnelles pour le moment{errorSuffix} — vous pouvez terminer sans elles et Lena disposera quand même de toutes les informations du formulaire.",
    "continue": "Continuer",
    "headerTitle": "Quelques questions pour que le compagnon se sente comme un membre de la famille",
    "headerSubFamily": "Ces questions concernent personnellement votre proche — répondez à celles qui vous viennent à l'esprit, passez les autres. Même une seule belle anecdote aide le compagnon à donner l'impression de le/la connaître depuis des années.",
    "headerSubSelf": "Ces questions vous concernent personnellement — répondez à celles qui vous viennent à l'esprit, passez les autres. Même une seule belle anecdote aide le compagnon à donner l'impression de vous connaître depuis des années.",
    "questionN": "Question {n}",
    "answerPlaceholder": "Votre réponse (facultatif)",
    "showDifferent": "Afficher d'autres questions",
    "generating": "Génération…",
    "skip": "Passer — j'ai terminé",
    "saveFinish": "Enregistrer et terminer",
    "errGenerate": "Impossible de générer les questions",
    "errSave": "Impossible d'enregistrer les réponses"
  },
  "allset": {
    "title": "Tout est prêt !",
    "subFamily": "{companion} est prêt(e) à rencontrer {name} !",
    "subSelf": "{companion} est prêt(e) à discuter avec vous !",
    "openTabletFamily": "Ouvrez ceci sur sa tablette",
    "openTabletSelf": "Ouvrez ceci sur votre tablette",
    "qrHelp": "Dirigez l'appareil photo de la tablette vers le QR code, ou ouvrez un navigateur et saisissez l'adresse ci-dessus. La tablette se jumellera automatiquement.",
    "reassurance": "Vous n'avez pas la tablette sous la main pour le moment ? Pas d'inquiétude — vous pourrez toujours récupérer ce lien depuis votre portail.",
    "goPortal": "Accéder à votre portail"
  },
  "toastLoadError": "Impossible de charger les données à vérifier",
  "saveFailed": "Échec de l'enregistrement"
};

const es = {
  "loading": "Cargando sus respuestas…",
  "common": {
    "optional": "(opcional)",
    "add": "Añadir",
    "addItem": "+ Añadir",
    "remove": "Quitar",
    "saving": "Guardando…"
  },
  "header": {
    "newTitleFamily": "Háblenos de su ser querido",
    "newTitleSelf": "Háblenos de usted",
    "newSubFamily": "Complete lo básico para que el compañero sepa cómo conversar con esa persona. Solo unos pocos campos son obligatorios (marcados con *) — el resto ayudan a que el compañero sea más interesante, pero siempre puede volver más tarde.",
    "newSubSelf": "Complete lo básico para que Lena sepa cómo conversar con usted. Solo unos pocos campos son obligatorios (marcados con *) — el resto la ayudan a ser más interesante, pero siempre puede volver más tarde.",
    "reviewTitle": "Revise sus respuestas",
    "reviewSub": "Compruebe todo lo de abajo y corrija cualquier cosa que esté mal. Siempre puede volver y editar más tarde desde el portal."
  },
  "validationSummaryTitle": "Faltan algunos campos obligatorios",
  "validation": {
    "firstNameFamily": "¿Cuál es su nombre?",
    "firstNameSelf": "¿Cuál es su nombre?",
    "birthdayMissingFamily": "Se necesita la fecha de cumpleaños para que Lena pueda felicitarle el día correcto.",
    "birthdayMissingSelf": "Se necesita su fecha de cumpleaños para que Lena pueda felicitarle el día correcto.",
    "birthdayAdultFamily": "Amigo Mio está pensado para adultos — por favor, compruebe la fecha de cumpleaños.",
    "birthdayAdultSelf": "Debe tener al menos 18 años para crear una cuenta.",
    "languageFamily": "Elija un idioma para que el compañero pueda hablar con esa persona.",
    "languageSelf": "Elija un idioma para que Lena pueda hablar con usted.",
    "interestsFamily": "Por favor, añada al menos 3 intereses (tiene {count}). Repártalos entre las categorías de abajo — son los temas de los que hablará el compañero.",
    "interestsSelf": "Por favor, añada al menos 3 intereses (tiene {count}). Repártalos entre las categorías de abajo — son los temas de los que Lena conversará con usted.",
    "familyFamily": "Añada al menos un familiar — los nombres y las relaciones dan base a la conversación.",
    "familySelf": "Añada al menos un familiar o amigo — los nombres y las relaciones dan base a la conversación."
  },
  "personal": {
    "section": "Datos personales",
    "firstName": "Nombre",
    "lastName": "Apellido",
    "preferredNameFamily": "Nombre preferido (cómo le gusta que le llamen)",
    "preferredNameSelf": "Nombre preferido (cómo le gustaría que le llamen)",
    "birthday": "Cumpleaños",
    "birthdayHelp": "Ayuda al compañero a elegir música, películas y temas propios de su época — y permite que Lena le felicite el cumpleaños el día correcto.",
    "under18Family": "Amigo Mio está pensado para usuarios adultos — por favor, compruebe la fecha de nacimiento. La cuenta no se creará hasta que esta corresponda a un adulto.",
    "under18Self": "Debe tener al menos 18 años para crear una cuenta. La cuenta no se creará hasta que la fecha de nacimiento corresponda a un adulto.",
    "language": "Idioma",
    "companionName": "Nombre del compañero",
    "companionNamePlaceholder": "Lena"
  },
  "location": {
    "section": "Ubicación",
    "cityCountry": "¿En qué ciudad y país vive ahora?",
    "cityCountryPlaceholder": "p. ej. Barcelona, España",
    "timezone": "Zona horaria",
    "timezoneHelp": "La usamos para saludarle a la hora adecuada del día y para programar las llamadas de recordatorio dentro de su jornada. Detectada desde su navegador — cámbiela si esa persona vive en otro lugar.",
    "otherPlaces": "Otros lugares donde ha vivido"
  },
  "interests": {
    "section": "Intereses",
    "sectionHint": "(al menos 3 entre las categorías de abajo — los temas de los que hablará el compañero)",
    "help": "Las categorías ayudan al compañero a saber de qué tipo es cada interés — cuándo poner música, cuándo mostrar una película o cuándo hablar de deporte. No hace falta rellenar todas las categorías — solo las que encajen.",
    "minCount": "{count} / 3 mínimo",
    "topicsToAvoid": "Temas que evitar",
    "buckets": {
      "music_artists": {
        "label": "Cantantes / grupos favoritos",
        "placeholder": "p. ej. Rocío Jurado, Frank Sinatra"
      },
      "music_genres": {
        "label": "Estilos / géneros musicales",
        "placeholder": "p. ej. clásica, copla, jazz de los años 50"
      },
      "sports_interests": {
        "label": "Deportes / equipos",
        "placeholder": "p. ej. Real Madrid, tenis de Wimbledon"
      },
      "screen_interests": {
        "label": "Películas / televisión / actores",
        "placeholder": "p. ej. Charles Chaplin, Casablanca"
      },
      "places_interests": {
        "label": "Lugares favoritos",
        "placeholder": "p. ej. la sierra de Madrid, Barcelona"
      },
      "hobbies": {
        "label": "Aficiones",
        "placeholder": "p. ej. jardinería, crucigramas"
      },
      "other_interests": {
        "label": "Otros intereses",
        "placeholder": "cualquier otra cosa que valga la pena saber"
      }
    }
  },
  "family": {
    "section": "Familia",
    "sectionHint": "(al menos uno — los nombres y las relaciones dan base a cada conversación)",
    "empty": "Aún no se ha añadido ningún familiar. Añada al menos uno — los nombres y las relaciones dan base a cada conversación.",
    "name": "Nombre",
    "namePlaceholder": "p. ej. María",
    "relation": "Relación",
    "whose": "¿De quién?",
    "whoseHintFamily": "(por defecto, su ser querido)",
    "whoseHintSelf": "(por defecto, usted)",
    "whosePlaceholderFamily": "Su ser querido",
    "whosePlaceholderSelf": "Usted",
    "stillWithUs": "¿Sigue con nosotros?",
    "statusAlive": "Sí",
    "statusDeceased": "Falleció",
    "notes": "Notas",
    "notesHint": "(opcional — cualquier cosa que el compañero deba saber)",
    "notesPlaceholder": "p. ej. vive en Barcelona, llama todos los domingos",
    "addBtn": "+ Añadir familiar",
    "readbackAlive": "{name}, que sigue con nosotros, es {relation} de {relTo}.",
    "readbackDeceased": "{name}, que ha fallecido, era {relation} de {relTo}."
  },
  "friends": {
    "section": "Amigos",
    "sectionHint": "(opcional — viejos amigos por los que Lena puede preguntar)",
    "empty": "Aún no se ha añadido ningún amigo. Es opcional, pero a Lena le encanta preguntar por los viejos amigos.",
    "name": "Nombre",
    "namePlaceholder": "p. ej. Juan Pérez",
    "status": "Situación",
    "statusInContact": "Siguen en contacto",
    "statusLostTouch": "Perdieron el contacto",
    "statusDeceased": "Falleció",
    "location": "Ubicación",
    "locationPlaceholder": "Dónde vive ahora",
    "howMet": "Cómo se conocieron",
    "howMetPlaceholder": "p. ej. amigos del colegio, vecinos, colegas",
    "sharedInterests": "Intereses en común",
    "sharedInterestsPlaceholder": "Qué solían hacer / de qué hablaban juntos",
    "addBtn": "+ Añadir amigo"
  },
  "pets": {
    "section": "Mascotas",
    "sectionHint": "(opcional — actuales y pasadas)",
    "empty": "Aún no se ha añadido ninguna mascota. Es opcional, pero las mascotas son de las cosas más entrañables por las que Lena puede preguntar.",
    "name": "Nombre",
    "namePlaceholder": "p. ej. Toby",
    "species": "Especie",
    "speciesPlaceholder": "Perro, gato, periquito…",
    "status": "Situación",
    "statusCurrent": "Actual",
    "statusPast": "Pasada",
    "note": "Nota",
    "notePlaceholder": "p. ej. rescatado de la perrera, le encantan los paseos",
    "addBtn": "+ Añadir mascota"
  },
  "faith": {
    "section": "Fe / espiritualidad",
    "tradition": "Tradición",
    "notSpecified": "(sin especificar)",
    "otherSpecify": "Otra (especificar)…",
    "traditionCustomPlaceholder": "Escriba la tradición",
    "traditionHelp": "Vacío / «ninguna» / «no religioso/a» significan todos que Lena no hará referencias a la fe.",
    "importance": "¿Qué importancia tiene en el día a día?",
    "importanceCentral": "Fundamental en la vida diaria",
    "importanceCultural": "Cultural / solo en fiestas",
    "importanceNotReally": "No forma parte realmente del día a día",
    "practicesFamily": "Prácticas y tradiciones que mantiene",
    "practicesSelf": "Prácticas y tradiciones que mantiene",
    "practicesPlaceholderFamily": "p. ej. va a la misa del Gallo con su hija; enciende velas por el Día de Todos los Santos.",
    "practicesPlaceholderSelf": "p. ej. va a la misa del Gallo con su hija; enciende velas por el Día de Todos los Santos."
  },
  "childhood": {
    "section": "Infancia",
    "growUpFamily": "Dónde se crió — familia, hogar, cualquier cosa que le marcara",
    "growUpSelf": "Dónde se crió — familia, hogar, cualquier cosa que le marcara",
    "growUpPlaceholderFamily": "p. ej. la mayor de cuatro hermanos en Toledo — su padre era panadero, veranos en Santander, aprendió a cocinar con su abuela. Omítalo si no es un tema agradable.",
    "growUpPlaceholderSelf": "p. ej. el mayor de cuatro hermanos en Toledo — su padre era panadero, veranos en Santander, aprendió a cocinar con su abuela. Omítalo si no es un tema agradable.",
    "sensitiveNotes": "Notas delicadas",
    "sensitiveBadge": "privado",
    "sensitivePlaceholder": "Cualquier cosa que Lena deba saber pero que NUNCA deba sacar a colación — p. ej. un padre severo, una pérdida, una época difícil.",
    "sensitiveHelp": "🔒 Cifrado, nunca se le muestra de vuelta ni se lee en voz alta. Lena lo recibe únicamente como orientación sobre lo que debe evitar."
  },
  "working": {
    "section": "Vida laboral",
    "profession": "Profesión o cargo",
    "professionPlaceholder": "p. ej. maestra de primaria",
    "employer": "Empleador",
    "employerPlaceholder": "p. ej. Colegio Santa María, Toledo",
    "industry": "Sector",
    "industryPlaceholder": "p. ej. Educación",
    "yearsActive": "Años en activo",
    "yearsActivePlaceholder": "p. ej. 1962 – 1998",
    "proudFamily": "¿De qué se siente más orgullosa?",
    "proudSelf": "¿De qué se siente más orgulloso/a?",
    "proudPlaceholderFamily": "p. ej. enseñó a leer a cientos de niños; todavía recibe cartas de antiguos alumnos.",
    "proudPlaceholderSelf": "p. ej. enseñó a leer a cientos de niños; todavía recibe cartas de antiguos alumnos."
  },
  "memories": {
    "section": "Recuerdos felices y momentos de la vida",
    "label": "Momentos o recuerdos especiales",
    "placeholder": "Acontecimientos memorables, viajes, hitos…"
  },
  "health": {
    "section": "Notas de salud y memoria",
    "ongoing": "Problemas de salud continuos",
    "ongoingHelp": "Dolencias de larga duración que el compañero debe tener presentes para el ritmo y la sensibilidad de la conversación (diabetes, artritis, pérdida de audición…). Las usa de forma discreta — no las sacará como tema de conversación.",
    "chronicEmpty": "Aún no se ha añadido ningún problema de salud continuo. Es opcional — añada solo cosas que no vayan a cambiar (diabetes, artritis, pérdida de audición, etc.). El compañero no las sacará directamente.",
    "chronicPlaceholder": "p. ej. diabetes tipo 2 desde 2015, bien controlada con medicación",
    "addCondition": "+ Añadir dolencia",
    "notesPrivate": "Notas para el compañero (privadas)",
    "notesPrivateHelp": "Orientación delicada que debe conocer pero nunca mencionar (un historial difícil, cosas que evitar). Se mantiene privada; nunca se repite de vuelta.",
    "notesPrivatePlaceholder": "Cualquier cosa que el compañero deba saber para adaptarse…"
  },
  "saveButton": "Guardar e ir a las preguntas de seguimiento",
  "followup": {
    "loadingTitle": "Redactando cinco preguntas sobre esa persona…",
    "loadingSub": "Esto suele tardar unos segundos.",
    "emptyTitle": "Un último paso…",
    "emptyBody": "No hemos podido generar las preguntas personales en este momento{errorSuffix} — puede terminar sin ellas y Lena tendrá igualmente todo lo del formulario.",
    "continue": "Continuar",
    "headerTitle": "Unas cuantas preguntas para que el compañero se sienta como de la familia",
    "headerSubFamily": "Estas son personales sobre su ser querido — responda las que se le ocurran y omita el resto. Aunque sea una sola buena anécdota, ayuda a que el compañero parezca conocerle desde hace años.",
    "headerSubSelf": "Estas son preguntas personales para usted — responda las que se le ocurran y omita el resto. Aunque sea una sola buena anécdota, ayuda a que el compañero parezca conocerle desde hace años.",
    "questionN": "Pregunta {n}",
    "answerPlaceholder": "Su respuesta (opcional)",
    "showDifferent": "Mostrar otras preguntas",
    "generating": "Generando…",
    "skip": "Omitir — he terminado",
    "saveFinish": "Guardar y finalizar",
    "errGenerate": "No se han podido generar las preguntas",
    "errSave": "No se han podido guardar las respuestas"
  },
  "allset": {
    "title": "¡Todo listo!",
    "subFamily": "¡{companion} está lista para conocer a {name}!",
    "subSelf": "¡{companion} está lista para conversar con usted!",
    "openTabletFamily": "Abra esto en la tableta de esa persona",
    "openTabletSelf": "Abra esto en su tableta",
    "qrHelp": "Apunte la cámara de la tableta al código QR, o abra un navegador y escriba la dirección de arriba. La tableta se emparejará automáticamente.",
    "reassurance": "¿No tiene la tableta a mano ahora mismo? No se preocupe — siempre puede volver a obtener este enlace desde su Portal.",
    "goPortal": "Ir a su portal"
  },
  "toastLoadError": "No se han podido cargar los datos de revisión",
  "saveFailed": "Error al guardar"
};

const it = {
  "loading": "Caricamento delle sue risposte…",
  "common": {
    "optional": "(facoltativo)",
    "add": "Aggiungi",
    "addItem": "+ Aggiungi",
    "remove": "Rimuovi",
    "saving": "Salvataggio…"
  },
  "header": {
    "newTitleFamily": "Ci parli della persona a cui vuole bene",
    "newTitleSelf": "Ci parli di Lei",
    "newSubFamily": "Inserisca le informazioni di base, così il compagno saprà come parlare con lei. Solo pochi campi sono obbligatori (contrassegnati con *) — gli altri aiutano il compagno a essere più interessante, ma può sempre tornare più tardi.",
    "newSubSelf": "Inserisca le informazioni di base, così Lena saprà come parlare con Lei. Solo pochi campi sono obbligatori (contrassegnati con *) — gli altri la aiutano a essere più interessante, ma può sempre tornare più tardi.",
    "reviewTitle": "Riveda le sue risposte",
    "reviewSub": "Controlli tutto qui sotto e corregga eventuali errori. Può sempre tornare e modificare in seguito dal portale."
  },
  "validationSummaryTitle": "Mancano alcuni campi obbligatori",
  "validation": {
    "firstNameFamily": "Qual è il suo nome?",
    "firstNameSelf": "Qual è il Suo nome?",
    "birthdayMissingFamily": "Il compleanno serve a Lena per fare gli auguri nel giorno giusto.",
    "birthdayMissingSelf": "Il Suo compleanno serve a Lena per farLe gli auguri nel giorno giusto.",
    "birthdayAdultFamily": "Amigo Mio è pensato per adulti — controlli la data di nascita.",
    "birthdayAdultSelf": "Deve avere almeno 18 anni per creare un account.",
    "languageFamily": "Scelga una lingua, così il compagno potrà parlare con lei.",
    "languageSelf": "Scelga una lingua, così Lena potrà parlare con Lei.",
    "interestsFamily": "Aggiunga almeno 3 interessi (ne ha {count}). Li distribuisca tra le categorie qui sotto — sono gli argomenti di cui il compagno parlerà.",
    "interestsSelf": "Aggiunga almeno 3 interessi (ne ha {count}). Li distribuisca tra le categorie qui sotto — sono gli argomenti di cui Lena chiacchiererà con Lei.",
    "familyFamily": "Aggiunga almeno un familiare — nomi e parentele danno concretezza alla conversazione.",
    "familySelf": "Aggiunga almeno un familiare o amico — nomi e parentele danno concretezza alla conversazione."
  },
  "personal": {
    "section": "Dati personali",
    "firstName": "Nome",
    "lastName": "Cognome",
    "preferredNameFamily": "Nome preferito (come le piace essere chiamata)",
    "preferredNameSelf": "Nome preferito (come Le piacerebbe essere chiamato)",
    "birthday": "Compleanno",
    "birthdayHelp": "Aiuta il compagno a scegliere musica, film e argomenti adatti alla sua epoca — e permette a Lena di farle gli auguri di buon compleanno nel giorno giusto.",
    "under18Family": "Amigo Mio è pensato per utenti adulti — controlli la data di nascita. L'account non sarà attivato finché questa non indicherà un adulto.",
    "under18Self": "Deve avere almeno 18 anni per creare un account. L'account non sarà attivato finché la data di nascita non indicherà un adulto.",
    "language": "Lingua",
    "companionName": "Nome del compagno",
    "companionNamePlaceholder": "Lena"
  },
  "location": {
    "section": "Luogo",
    "cityCountry": "In quale città e Paese vive adesso?",
    "cityCountryPlaceholder": "es. Barcellona, Spagna",
    "timezone": "Fuso orario",
    "timezoneHelp": "Lo usiamo per salutarla all'ora giusta della giornata e per programmare le chiamate di promemoria nell'arco della sua giornata. Rilevato dal suo browser — lo modifichi se vive altrove.",
    "otherPlaces": "Altri luoghi in cui ha vissuto"
  },
  "interests": {
    "section": "Interessi",
    "sectionHint": "(almeno 3 tra le categorie qui sotto — gli argomenti di cui il compagno parlerà)",
    "help": "Le categorie aiutano il compagno a capire di che tipo di interesse si tratta — quando mettere musica, quando mostrare un film, quando parlare di sport. Non deve riempire ogni categoria — solo quelle che si adattano.",
    "minCount": "{count} / 3 minimo",
    "topicsToAvoid": "Argomenti da evitare",
    "buckets": {
      "music_artists": {
        "label": "Cantanti / gruppi preferiti",
        "placeholder": "es. Queen, Frank Sinatra"
      },
      "music_genres": {
        "label": "Stili / generi musicali",
        "placeholder": "es. classica, jazz anni '50"
      },
      "sports_interests": {
        "label": "Sport / squadre",
        "placeholder": "es. Juventus, tennis di Wimbledon"
      },
      "screen_interests": {
        "label": "Film / TV / attori",
        "placeholder": "es. Totò, Casablanca"
      },
      "places_interests": {
        "label": "Luoghi preferiti",
        "placeholder": "es. lago di Como, Barcellona"
      },
      "hobbies": {
        "label": "Hobby",
        "placeholder": "es. giardinaggio, parole crociate"
      },
      "other_interests": {
        "label": "Altri interessi",
        "placeholder": "qualsiasi altra cosa utile da sapere"
      }
    }
  },
  "family": {
    "section": "Famiglia",
    "sectionHint": "(almeno uno — nomi e parentele danno concretezza a ogni conversazione)",
    "empty": "Nessun familiare ancora aggiunto. Ne aggiunga almeno uno — nomi e parentele danno concretezza a ogni conversazione.",
    "name": "Nome",
    "namePlaceholder": "es. Maria",
    "relation": "Parentela",
    "whose": "Di chi?",
    "whoseHintFamily": "(per impostazione predefinita, la persona a cui vuole bene)",
    "whoseHintSelf": "(per impostazione predefinita, Lei)",
    "whosePlaceholderFamily": "La persona a cui vuole bene",
    "whosePlaceholderSelf": "Lei",
    "stillWithUs": "È ancora tra noi?",
    "statusAlive": "Sì",
    "statusDeceased": "È venuto a mancare",
    "notes": "Note",
    "notesHint": "(facoltativo — qualsiasi cosa il compagno dovrebbe sapere)",
    "notesPlaceholder": "es. vive a Barcellona, chiama ogni domenica",
    "addBtn": "+ Aggiungi familiare",
    "readbackAlive": "{name}, che è ancora tra noi, è {relation} di {relTo}.",
    "readbackDeceased": "{name}, che è venuto a mancare, era {relation} di {relTo}."
  },
  "friends": {
    "section": "Amici",
    "sectionHint": "(facoltativo — vecchi amici di cui Lena può chiedere notizie)",
    "empty": "Nessun amico ancora aggiunto. Facoltativo, ma a Lena piace molto chiedere notizie dei vecchi amici.",
    "name": "Nome",
    "namePlaceholder": "es. Gianni Rossi",
    "status": "Stato",
    "statusInContact": "Ancora in contatto",
    "statusLostTouch": "Persi di vista",
    "statusDeceased": "È venuto a mancare",
    "location": "Luogo",
    "locationPlaceholder": "Dove vive adesso",
    "howMet": "Come si sono conosciuti",
    "howMetPlaceholder": "es. compagni di scuola, vicini di casa, colleghi",
    "sharedInterests": "Interessi in comune",
    "sharedInterestsPlaceholder": "Cosa facevano / di cosa parlavano insieme",
    "addBtn": "+ Aggiungi amico"
  },
  "pets": {
    "section": "Animali domestici",
    "sectionHint": "(facoltativo — attuali e passati)",
    "empty": "Nessun animale ancora aggiunto. Facoltativo, ma gli animali sono tra le cose più care di cui Lena può chiedere.",
    "name": "Nome",
    "namePlaceholder": "es. Fido",
    "species": "Specie",
    "speciesPlaceholder": "Cane, Gatto, Pappagallino…",
    "status": "Stato",
    "statusCurrent": "Attuale",
    "statusPast": "Passato",
    "note": "Nota",
    "notePlaceholder": "es. preso al canile, adora le passeggiate",
    "addBtn": "+ Aggiungi animale"
  },
  "faith": {
    "section": "Fede / spiritualità",
    "tradition": "Tradizione",
    "notSpecified": "(non specificata)",
    "otherSpecify": "Altro (specificare)…",
    "traditionCustomPlaceholder": "Scriva la tradizione",
    "traditionHelp": "Vuoto / \"nessuna\" / \"non religioso\" significano tutti che Lena non farà alcun riferimento alla fede.",
    "importance": "Quanto è importante nella vita di tutti i giorni?",
    "importanceCentral": "Centrale nella vita quotidiana",
    "importanceCultural": "Solo aspetto culturale / festività",
    "importanceNotReally": "Non fa davvero parte della vita quotidiana",
    "practicesFamily": "Pratiche e tradizioni che mantiene",
    "practicesSelf": "Pratiche e tradizioni che mantiene",
    "practicesPlaceholderFamily": "es. va alla Messa della Vigilia di Natale con la figlia; accende candele per Ognissanti.",
    "practicesPlaceholderSelf": "es. va alla Messa della Vigilia di Natale con la figlia; accende candele per Ognissanti."
  },
  "childhood": {
    "section": "Infanzia",
    "growUpFamily": "Dove è cresciuta — famiglia, casa, tutto ciò che l'ha formata",
    "growUpSelf": "Dove è cresciuto — famiglia, casa, tutto ciò che L'ha formato",
    "growUpPlaceholderFamily": "es. la maggiore di quattro figli a Firenze — padre minatore, estati in Versilia, ha imparato a cucinare con la nonna. Salti se non è un argomento sereno.",
    "growUpPlaceholderSelf": "es. il maggiore di quattro figli a Firenze — padre minatore, estati in Versilia, ha imparato a cucinare con la nonna. Salti se non è un argomento sereno.",
    "sensitiveNotes": "Note delicate",
    "sensitiveBadge": "privato",
    "sensitivePlaceholder": "Qualcosa che Lena dovrebbe sapere ma che non dovrebbe MAI tirare fuori — es. un genitore severo, un lutto, un periodo difficile.",
    "sensitiveHelp": "🔒 Criptato, mai mostrato a Lei né letto ad alta voce. Lena lo riceve solo come indicazione su cosa evitare."
  },
  "working": {
    "section": "Vita lavorativa",
    "profession": "Professione o ruolo",
    "professionPlaceholder": "es. maestra di scuola elementare",
    "employer": "Datore di lavoro",
    "employerPlaceholder": "es. Scuola elementare San Giorgio, Firenze",
    "industry": "Settore",
    "industryPlaceholder": "es. Istruzione",
    "yearsActive": "Anni di attività",
    "yearsActivePlaceholder": "es. 1962 – 1998",
    "proudFamily": "Di cosa è più orgogliosa?",
    "proudSelf": "Di cosa è più orgoglioso?",
    "proudPlaceholderFamily": "es. ha avviato alla lettura centinaia di bambini; riceve ancora lettere dagli ex alunni.",
    "proudPlaceholderSelf": "es. ha avviato alla lettura centinaia di bambini; riceve ancora lettere dagli ex alunni."
  },
  "memories": {
    "section": "Bei ricordi ed eventi della vita",
    "label": "Momenti o ricordi speciali",
    "placeholder": "Eventi memorabili, viaggi, traguardi…"
  },
  "health": {
    "section": "Note su salute e memoria",
    "ongoing": "Condizioni di salute in corso",
    "ongoingHelp": "Condizioni di lunga data di cui il compagno dovrebbe essere a conoscenza per ritmo e delicatezza (diabete, artrite, perdita dell'udito…). Le usa in silenzio — non le tirerà fuori come argomento.",
    "chronicEmpty": "Nessuna condizione di salute in corso ancora aggiunta. Facoltativo — aggiunga solo cose che non cambieranno (diabete, artrite, perdita dell'udito, ecc.). Il compagno non le tirerà fuori direttamente.",
    "chronicPlaceholder": "es. diabete di tipo 2 dal 2015, ben controllato con i farmaci",
    "addCondition": "+ Aggiungi condizione",
    "notesPrivate": "Note per il compagno (private)",
    "notesPrivateHelp": "Indicazioni delicate che dovrebbe conoscere ma mai sollevare (una storia difficile, cose da evitare). Restano private; mai ripetute.",
    "notesPrivatePlaceholder": "Qualsiasi cosa il compagno dovrebbe sapere per adattarsi…"
  },
  "saveButton": "Salva e passa alle domande di approfondimento",
  "followup": {
    "loadingTitle": "Stiamo scrivendo cinque domande su di lei…",
    "loadingSub": "Di solito ci vogliono pochi secondi.",
    "emptyTitle": "Un ultimo passaggio…",
    "emptyBody": "Non siamo riusciti a generare le domande personali in questo momento{errorSuffix} — può terminare senza di esse e Lena avrà comunque tutto ciò che ha inserito nel modulo.",
    "continue": "Continua",
    "headerTitle": "Qualche domanda per far sentire il compagno come uno di famiglia",
    "headerSubFamily": "Queste domande sono personali per la persona a cui vuole bene — risponda a quelle che le vengono in mente, salti le altre. Anche solo un bel racconto aiuta il compagno a sembrare di conoscerla da anni.",
    "headerSubSelf": "Queste sono domande personali per Lei — risponda a quelle che Le vengono in mente, salti le altre. Anche solo un bel racconto aiuta il compagno a sembrare di conoscerLa da anni.",
    "questionN": "Domanda {n}",
    "answerPlaceholder": "La sua risposta (facoltativa)",
    "showDifferent": "Mostra domande diverse",
    "generating": "Generazione…",
    "skip": "Salta — ho finito",
    "saveFinish": "Salva e termina",
    "errGenerate": "Impossibile generare le domande",
    "errSave": "Impossibile salvare le risposte"
  },
  "allset": {
    "title": "È tutto pronto!",
    "subFamily": "{companion} è pronta a conoscere {name}!",
    "subSelf": "{companion} è pronta a chiacchierare con Lei!",
    "openTabletFamily": "Apra questo sul suo tablet",
    "openTabletSelf": "Apra questo sul Suo tablet",
    "qrHelp": "Punti la fotocamera del tablet sul codice QR, oppure apra un browser e digiti l'indirizzo qui sopra. Il tablet si abbinerà automaticamente.",
    "reassurance": "Non ha il tablet con sé in questo momento? Nessun problema — può sempre recuperare questo link dal Suo Portale.",
    "goPortal": "Vai al tuo portale"
  },
  "toastLoadError": "Impossibile caricare i dati da rivedere",
  "saveFailed": "Salvataggio non riuscito"
};

const pt = {
  "loading": "A carregar as suas respostas…",
  "common": {
    "optional": "(opcional)",
    "add": "Adicionar",
    "addItem": "+ Adicionar",
    "remove": "Remover",
    "saving": "A guardar…"
  },
  "header": {
    "newTitleFamily": "Fale-nos do seu ente querido",
    "newTitleSelf": "Fale-nos de si",
    "newSubFamily": "Preencha o essencial para que o companheiro saiba como conversar com ele. Apenas alguns campos são obrigatórios (assinalados com *) — os restantes ajudam o companheiro a ser mais interessante, mas pode sempre voltar mais tarde.",
    "newSubSelf": "Preencha o essencial para que a Lena saiba como conversar consigo. Apenas alguns campos são obrigatórios (assinalados com *) — os restantes ajudam-na a ser mais interessante, mas pode sempre voltar mais tarde.",
    "reviewTitle": "Reveja as suas respostas",
    "reviewSub": "Confira tudo o que se segue e corrija o que estiver errado. Pode sempre voltar e editar mais tarde através do portal."
  },
  "validationSummaryTitle": "Faltam alguns campos obrigatórios",
  "validation": {
    "firstNameFamily": "Qual é o primeiro nome dele?",
    "firstNameSelf": "Qual é o seu primeiro nome?",
    "birthdayMissingFamily": "É necessária a data de nascimento para que a Lena possa felicitá-lo no dia certo.",
    "birthdayMissingSelf": "É necessária a sua data de nascimento para que a Lena possa felicitá-lo no dia certo.",
    "birthdayAdultFamily": "O Amigo Mio foi feito para adultos — por favor, verifique a data de nascimento.",
    "birthdayAdultSelf": "Tem de ter pelo menos 18 anos para criar uma conta.",
    "languageFamily": "Escolha um idioma para que o companheiro possa falar com ele.",
    "languageSelf": "Escolha um idioma para que a Lena possa falar consigo.",
    "interestsFamily": "Por favor, adicione pelo menos 3 interesses (tem {count}). Distribua-os pelas categorias abaixo — é sobre estes que o companheiro vai conversar.",
    "interestsSelf": "Por favor, adicione pelo menos 3 interesses (tem {count}). Distribua-os pelas categorias abaixo — é sobre estes que a Lena vai conversar consigo.",
    "familyFamily": "Adicione pelo menos um familiar — nomes e relações dão base à conversa.",
    "familySelf": "Adicione pelo menos um familiar ou amigo — nomes e relações dão base à conversa."
  },
  "personal": {
    "section": "Dados pessoais",
    "firstName": "Primeiro nome",
    "lastName": "Apelido",
    "preferredNameFamily": "Nome preferido (como gosta de ser tratado)",
    "preferredNameSelf": "Nome preferido (como gostaria de ser tratado)",
    "birthday": "Data de nascimento",
    "birthdayHelp": "Ajuda o companheiro a escolher música, filmes e temas adequados à época — e permite que a Lena lhe deseje feliz aniversário no dia certo.",
    "under18Family": "O Amigo Mio foi feito para utilizadores adultos — por favor, verifique a data de nascimento. A conta só será criada quando esta indicar um adulto.",
    "under18Self": "Tem de ter pelo menos 18 anos para criar uma conta. A conta só será criada quando a data de nascimento indicar um adulto.",
    "language": "Idioma",
    "companionName": "Nome do companheiro",
    "companionNamePlaceholder": "Lena"
  },
  "location": {
    "section": "Localização",
    "cityCountry": "Em que cidade e país vive atualmente?",
    "cityCountryPlaceholder": "ex.: Lisboa, Portugal",
    "timezone": "Fuso horário",
    "timezoneHelp": "Usamos isto para o cumprimentar à hora certa do dia e para agendar chamadas de lembrete ao longo do seu dia. Detetado a partir do seu navegador — altere-o se viver noutro local.",
    "otherPlaces": "Outros lugares onde viveu"
  },
  "interests": {
    "section": "Interesses",
    "sectionHint": "(pelo menos 3 distribuídos pelas categorias abaixo — sobre o que o companheiro vai conversar)",
    "help": "As categorias ajudam o companheiro a perceber que tipo de interesse é cada um — quando pôr música, quando mostrar um filme ou quando falar de desporto. Não precisa de preencher todas as categorias — apenas as que se aplicam.",
    "minCount": "{count} / 3 mínimo",
    "topicsToAvoid": "Temas a evitar",
    "buckets": {
      "music_artists": {
        "label": "Cantores / bandas favoritos",
        "placeholder": "ex.: Amália Rodrigues, Frank Sinatra"
      },
      "music_genres": {
        "label": "Estilos / géneros musicais",
        "placeholder": "ex.: música clássica, fado"
      },
      "sports_interests": {
        "label": "Desportos / equipas",
        "placeholder": "ex.: Benfica, ténis de Wimbledon"
      },
      "screen_interests": {
        "label": "Filmes / TV / atores",
        "placeholder": "ex.: Charlie Chaplin, Casablanca"
      },
      "places_interests": {
        "label": "Lugares favoritos",
        "placeholder": "ex.: Serra da Estrela, Algarve"
      },
      "hobbies": {
        "label": "Passatempos",
        "placeholder": "ex.: jardinagem, palavras cruzadas"
      },
      "other_interests": {
        "label": "Outros interesses",
        "placeholder": "qualquer outra coisa que valha a pena saber"
      }
    }
  },
  "family": {
    "section": "Família",
    "sectionHint": "(pelo menos um — nomes + relações dão base a cada conversa)",
    "empty": "Ainda não foram adicionados familiares. Adicione pelo menos um — nomes + relações dão base a cada conversa.",
    "name": "Nome",
    "namePlaceholder": "ex.: Maria",
    "relation": "Relação",
    "whose": "De quem?",
    "whoseHintFamily": "(predefinido para o seu ente querido)",
    "whoseHintSelf": "(predefinido para si)",
    "whosePlaceholderFamily": "O seu ente querido",
    "whosePlaceholderSelf": "Si",
    "stillWithUs": "Ainda está connosco?",
    "statusAlive": "Sim",
    "statusDeceased": "Já faleceu",
    "notes": "Notas",
    "notesHint": "(opcional — algo que o companheiro deva saber)",
    "notesPlaceholder": "ex.: vive em Lisboa, telefona todos os domingos",
    "addBtn": "+ Adicionar familiar",
    "readbackAlive": "{name}, que ainda está connosco, é {relation} de {relTo}.",
    "readbackDeceased": "{name}, que já faleceu, era {relation} de {relTo}."
  },
  "friends": {
    "section": "Amigos",
    "sectionHint": "(opcional — velhos amigos por quem a Lena pode perguntar)",
    "empty": "Ainda não foram adicionados amigos. É opcional, mas a Lena adora perguntar por velhos amigos.",
    "name": "Nome",
    "namePlaceholder": "ex.: João Pereira",
    "status": "Estado",
    "statusInContact": "Ainda em contacto",
    "statusLostTouch": "Perdeu o contacto",
    "statusDeceased": "Já faleceu",
    "location": "Localização",
    "locationPlaceholder": "Onde vive atualmente",
    "howMet": "Como se conheceram",
    "howMetPlaceholder": "ex.: amigos de escola, vizinhos, colegas",
    "sharedInterests": "Interesses em comum",
    "sharedInterestsPlaceholder": "O que costumavam fazer / falar em conjunto",
    "addBtn": "+ Adicionar amigo"
  },
  "pets": {
    "section": "Animais de estimação",
    "sectionHint": "(opcional — atuais e antigos)",
    "empty": "Ainda não foram adicionados animais de estimação. É opcional, mas os animais são uma das coisas mais ternas por que a Lena pode perguntar.",
    "name": "Nome",
    "namePlaceholder": "ex.: Bobi",
    "species": "Espécie",
    "speciesPlaceholder": "Cão, Gato, Periquito…",
    "status": "Estado",
    "statusCurrent": "Atual",
    "statusPast": "Antigo",
    "note": "Nota",
    "notePlaceholder": "ex.: resgatado do canil, adora passeios",
    "addBtn": "+ Adicionar animal"
  },
  "faith": {
    "section": "Fé / espiritualidade",
    "tradition": "Tradição",
    "notSpecified": "(não especificado)",
    "otherSpecify": "Outra (especificar)…",
    "traditionCustomPlaceholder": "Escreva a tradição",
    "traditionHelp": "Vazio / \"nenhuma\" / \"não religioso\" significam que a Lena não fará quaisquer referências religiosas.",
    "importance": "Que importância tem no dia a dia?",
    "importanceCentral": "Central na vida diária",
    "importanceCultural": "Apenas cultural / festividades",
    "importanceNotReally": "Não faz realmente parte do dia a dia",
    "practicesFamily": "Práticas e tradições que ela mantém",
    "practicesSelf": "Práticas e tradições que mantém",
    "practicesPlaceholderFamily": "ex.: vai à Missa do Galo com a filha; acende velas no Dia de Todos os Santos.",
    "practicesPlaceholderSelf": "ex.: vai à Missa do Galo com a filha; acende velas no Dia de Todos os Santos."
  },
  "childhood": {
    "section": "Infância",
    "growUpFamily": "Onde cresceu — família, casa, tudo o que o marcou",
    "growUpSelf": "Onde cresceu — família, casa, tudo o que o marcou",
    "growUpPlaceholderFamily": "ex.: a mais velha de quatro irmãos em Coimbra — o pai era ferroviário, verões em Nazaré, aprendeu a cozinhar com a avó. Salte se não for uma área feliz.",
    "growUpPlaceholderSelf": "ex.: a mais velha de quatro irmãos em Coimbra — o pai era ferroviário, verões na Nazaré, aprendeu a cozinhar com a sua avó. Salte se não for uma área feliz.",
    "sensitiveNotes": "Notas delicadas",
    "sensitiveBadge": "privado",
    "sensitivePlaceholder": "Algo que a Lena deva saber, mas que NUNCA deva mencionar — ex.: um progenitor severo, uma perda, um período difícil.",
    "sensitiveHelp": "🔒 Encriptado, nunca lhe é mostrado nem lido em voz alta. A Lena recebe isto apenas como orientação sobre o que evitar."
  },
  "working": {
    "section": "Vida profissional",
    "profession": "Profissão ou função",
    "professionPlaceholder": "ex.: professora do ensino básico",
    "employer": "Entidade empregadora",
    "employerPlaceholder": "ex.: Escola Básica de São Mateus, Coimbra",
    "industry": "Setor de atividade",
    "industryPlaceholder": "ex.: Educação",
    "yearsActive": "Anos de atividade",
    "yearsActivePlaceholder": "ex.: 1962 – 1998",
    "proudFamily": "Do que é que ela tem mais orgulho?",
    "proudSelf": "De que é que tem mais orgulho?",
    "proudPlaceholderFamily": "ex.: ensinou centenas de crianças a ler; ainda recebe cartas de antigos alunos.",
    "proudPlaceholderSelf": "ex.: ensinou centenas de crianças a ler; ainda recebe cartas de antigos alunos."
  },
  "memories": {
    "section": "Memórias felizes e momentos de vida",
    "label": "Momentos ou memórias especiais",
    "placeholder": "Acontecimentos memoráveis, viagens, marcos importantes…"
  },
  "health": {
    "section": "Notas de saúde e memória",
    "ongoing": "Problemas de saúde permanentes",
    "ongoingHelp": "Condições de longa duração de que o companheiro deva ter conhecimento, para ajustar o ritmo e a sensibilidade (diabetes, artrite, perda de audição…). Ela usa-as de forma discreta — não as trará à conversa como tema.",
    "chronicEmpty": "Ainda não foram adicionados problemas de saúde permanentes. É opcional — adicione apenas situações que não irão mudar (diabetes, artrite, perda de audição, etc.). O companheiro não as mencionará diretamente.",
    "chronicPlaceholder": "ex.: diabetes tipo 2 desde 2015, bem controlada com medicação",
    "addCondition": "+ Adicionar condição",
    "notesPrivate": "Notas para o companheiro (privado)",
    "notesPrivateHelp": "Orientações delicadas que ela deva conhecer, mas nunca abordar (um historial difícil, coisas a evitar). Mantém-se privado; nunca é repetido.",
    "notesPrivatePlaceholder": "Algo que o companheiro deva saber para se adaptar…"
  },
  "saveButton": "Guardar e ir para as perguntas de seguimento",
  "followup": {
    "loadingTitle": "A escrever cinco perguntas sobre ele…",
    "loadingSub": "Isto demora normalmente alguns segundos.",
    "emptyTitle": "Um último passo…",
    "emptyBody": "Não foi possível gerar as perguntas pessoais neste momento{errorSuffix} — pode concluir sem elas e a Lena terá na mesma toda a informação do formulário.",
    "continue": "Continuar",
    "headerTitle": "Algumas perguntas para que o companheiro se sinta como família",
    "headerSubFamily": "Estas são pessoais para o seu ente querido — responda às que lhe ocorrerem e salte as restantes. Mesmo uma boa história ajuda o companheiro a soar como se o conhecesse há anos.",
    "headerSubSelf": "Estas são perguntas pessoais para si — responda às que lhe ocorrerem e salte as restantes. Mesmo uma boa história ajuda o companheiro a soar como se o conhecesse há anos.",
    "questionN": "Pergunta {n}",
    "answerPlaceholder": "A sua resposta (opcional)",
    "showDifferent": "Mostrar perguntas diferentes",
    "generating": "A gerar…",
    "skip": "Saltar — já terminei",
    "saveFinish": "Guardar e concluir",
    "errGenerate": "Não foi possível gerar as perguntas",
    "errSave": "Não foi possível guardar as respostas"
  },
  "allset": {
    "title": "Está tudo pronto!",
    "subFamily": "A {companion} está pronta para conhecer {name}!",
    "subSelf": "A {companion} está pronta para conversar consigo!",
    "openTabletFamily": "Abra isto no tablet dele",
    "openTabletSelf": "Abra isto no seu tablet",
    "qrHelp": "Aponte a câmara do tablet para o código QR ou abra um navegador e escreva o endereço acima. O tablet será emparelhado automaticamente.",
    "reassurance": "Não tem o tablet à mão neste momento? Não se preocupe — pode sempre obter de novo esta ligação a partir do seu Portal.",
    "goPortal": "Ir para o seu portal"
  },
  "toastLoadError": "Não foi possível carregar os dados da revisão",
  "saveFailed": "Falha ao guardar"
};

const INTAKE_REVIEW_STRINGS = { en, nl, de, fr, es, it, pt };

export default INTAKE_REVIEW_STRINGS;
